<template>
  <div class="protected-page">
    <div class="properties-page">
      <div class="page-title">Property Page</div>

      <!-- Tab Header -->
      <div class="tab-header">
        <button
          @click="switchTab('overview')"
          :class="{ active: activeTab === 'overview' }"
        >
          Overview
        </button>
        <button
          @click="switchTab('endings')"
          :class="{ active: activeTab === 'endings' }"
        >
          Lease Endings
          <span class="le-year-buttons">
            <button
              v-for="yearOffset in [2, 1, 0]"
              :key="'le-year-' + yearOffset"
              class="le-year-button"
              :class="{
                'le-year-selected':
                  leaseEndingsYear === new Date().getFullYear() - yearOffset,
                'le-year-hidden': activeTab !== 'endings',
              }"
              @click.stop="
                setLeaseEndingsYear(new Date().getFullYear() - yearOffset)
              "
              :title="`Filter by year ${new Date().getFullYear() - yearOffset}`"
            >
              {{ String(new Date().getFullYear() - yearOffset).slice(-2) }}
            </button>
          </span>
        </button>
        <button
          @click="switchTab('resigns')"
          :class="{ active: activeTab === 'resigns' }"
        >
          Early Resigns
        </button>
        <button
          @click="switchTab('leasesByMonth')"
          :class="{ active: activeTab === 'leasesByMonth' }"
        >
          Leases by Month
        </button>
        <button
          @click="switchTab('payments')"
          :class="{ active: activeTab === 'payments' }"
        >
          Payments
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Tab 1: overview and endings (shared content) -->
        <div
          v-if="activeTab === 'overview' || activeTab === 'endings'"
          style="margin-top: -20px"
          :id="activeTab === 'endings' ? 'endingsTab' : 'overviewTab'"
        >
          <div class="search-input-wrapper">
            <!-- Property Search Bar for table -->
            <div class="search-bar">
              <div class="search-left">
                <input
                  style="width: 80px; margin-right: 10px"
                  ref="searchQuery"
                  v-model="searchQuery"
                  @input="onSearch"
                  @keydown.esc="if (!searchQuery) $refs.searchQuery.blur();"
                  type="text"
                  placeholder="Search for property or tenant (use commas to refine)"
                  class="search-input"
                />
                <button @click="clearSearch" class="clear-button">X</button>
                <button
                  :title="'find\nVacant'"
                  @click="searchQuery = ':v'"
                  class="clear-button"
                >
                  <i class="fas fa-wine-glass-empty"></i>
                </button>
                <button
                  :title="'find\nnon-renewed'"
                  @click="
                    switchTab('endings');
                    searchQuery = ':lenr';
                  "
                  class="clear-button"
                >
                  <i class="fas fa-hands"></i>
                </button>
                <button
                  :title="'tenant\nleaving soon'"
                  @click="searchQuery = ':tl'"
                  class="clear-button"
                >
                  <i class="fas fa-hand-peace"></i>
                </button>
                <button
                  :title="'expiring no re-sign'"
                  @click="cycleExpirationFilter"
                  class="clear-button"
                  style="position: relative"
                >
                  <i class="fas fa-cancel"></i>
                  <!-- Visual indicators for filter state -->
                  <span
                    v-if="expirationFilterState === ':ex30'"
                    style="
                      position: absolute;
                      bottom: -11px;
                      left: 50%;
                      transform: translateX(-50%);
                      font-size: 10px;
                      color: #333;
                      font-weight: bold;
                    "
                    >30</span
                  >
                  <span
                    v-if="expirationFilterState === ':ex60'"
                    style="
                      position: absolute;
                      bottom: -11px;
                      left: 50%;
                      transform: translateX(-50%);
                      font-size: 10px;
                      color: #333;
                      font-weight: bold;
                    "
                    >60</span
                  >
                  <span
                    v-if="expirationFilterState === ':exAll'"
                    style="
                      position: absolute;
                      bottom: -11px;
                      left: 50%;
                      transform: translateX(-50%);
                      font-size: 10px;
                      color: #333;
                      font-weight: bold;
                    "
                    >all</span
                  >
                </button>
                <button
                  :title="'find\nnon-Vacant'"
                  @click="searchQuery = ':!v'"
                  class="clear-button"
                >
                  <i class="fas fa-wine-glass"></i>
                </button>
                <button
                  :title="'find\nEarly'"
                  @click="searchQuery = ':e'"
                  class="clear-button"
                >
                  <i class="fas fa-kiwi-bird"></i>
                </button>
                <button
                  :title="'find\nlate pay'"
                  @click="searchQuery = ':lp'"
                  class="clear-button"
                >
                  <i class="fas fa-gavel"></i>
                </button>
                <button
                  :title="'find\nBal Ratios > 1'"
                  @click="searchQuery = ':!br'"
                  class="clear-button"
                >
                  <i class="fas fa-circle-exclamation"></i>
                </button>
                <button
                  :title="'find\ninvalid payments'"
                  @click="searchQuery = ':invP'"
                  class="clear-button"
                >
                  <i class="fas fa-poo"></i>
                </button>
                <button
                  :title="'find\nneg. Bal.'"
                  @click="searchQuery = ':!b'"
                  class="clear-button"
                >
                  <i class="fas fa-balance-scale-left"></i>
                </button>
                <button
                  :title="'find\nzero. Bal.'"
                  @click="searchQuery = ':z'"
                  class="clear-button"
                >
                  <i class="fas fa-scale-balanced"></i>
                </button>
                <button
                  :title="'find\npos. Bal.'"
                  @click="searchQuery = ':b'"
                  class="clear-button"
                >
                  <i class="fas fa-balance-scale-right"></i>
                </button>
                <button
                  :title="'sort by\naging'"
                  @click="searchQuery = ':p'"
                  class="clear-button"
                >
                  <i class="fas fa-history"></i>
                </button>
                <button
                  :title="'find\nstatuses'"
                  @click="searchQuery = ':s'"
                  class="clear-button"
                >
                  <i class="fas fa-radiation"></i>
                </button>
                <button
                  :title="'sort checked\nto top'"
                  @click="sortChecked"
                  class="clear-button"
                >
                  <i class="fas fa-check"></i>
                </button>
                <button
                  :title="'filter only selected'"
                  @click="searchQuery = ':sel'"
                  class="clear-button"
                >
                  <i class="fas fa-vote-yea"></i>
                </button>
                <button
                  :title="'export to excel'"
                  @click="exportToExcel"
                  class="clear-button"
                >
                  <i class="fas fa-file-excel"></i>
                </button>
                <button
                  :title="'copy selected rows as markdown'"
                  @click="copySelectedAsMarkdown"
                  class="clear-button"
                >
                  <i class="fas fa-table"></i>
                </button>
                <button
                  :title="'print list'"
                  @click="printTable"
                  class="clear-button"
                >
                  <i class="fas fa-print"></i>
                </button>
                <label></label
                ><span>{{ this.selectedProperties.length }}</span> of
                <span>{{ this.filteredPropertiesTotal.length - 1 }}</span>
              </div>
              <div class="search-right">
                <div class="input-group">
                  <div title="set all checkmarks">
                    <button @click="resetAllCheckmarks('set')">
                      {{ 'Set' }} <i class="fas fa-check"></i>
                    </button>
                  </div>
                  <div title="clear all checkmarks">
                    <button @click="resetAllCheckmarks('clear')">
                      {{ 'Clr' }} <i class="fas fa-check"></i>
                    </button>
                  </div>
                  <div class="recent-start">
                    <label for="startingSoon">start in</label>
                    <input
                      id="startingSoon"
                      v-model.number="startingWithin"
                      type="number"
                      min="1"
                      placeholder="14"
                      class="expiring-days-input"
                    />
                    <span>days</span>
                  </div>
                  <div class="expiring-input">
                    <label for="expiringWithin">expires in</label>
                    <input
                      id="expiringWithin"
                      v-model.number="expiringWithin"
                      type="number"
                      min="1"
                      placeholder="90"
                      class="expiring-days-input"
                    />
                    <span>days</span>
                  </div>
                  <button class="" @click="getProperties()">
                    <i class="fas fa-refresh"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="table-container-outer">
            <div class="table-container">
              <!-- Fixed header container -->
              <div class="table-header" ref="propertyTableHeader">
                <table>
                  <thead>
                    <tr>
                      <th
                        v-for="(header, index) in propertyTableHeaders"
                        :key="index"
                        :style="{
                          minWidth: header.width,
                          maxWidth: header.width,
                          textAlign:
                            header.align === 'right'
                              ? 'right'
                              : header.align === 'left'
                                ? 'left'
                                : 'center',
                        }"
                      >
                        <i v-if="header.icon" :class="header.icon"></i>
                        <template v-else>{{ header.label }}</template>
                        <div @click="sortBy(header.field)">
                          <i
                            class="fas"
                            :class="{
                              'fa-sort': sortField !== header.field,
                              'fa-sort-up':
                                sortField === header.field &&
                                sortOrder === 'asc',
                              'fa-sort-down':
                                sortField === header.field &&
                                sortOrder === 'desc',
                            }"
                            title="Sort"
                          ></i>
                        </div>
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              <!-- Scrollable body container -->
              <div class="table-body" ref="propertyTableBody">
                <table>
                  <tbody>
                    <tr
                      v-for="(property, index) in filteredPropertiesTotal"
                      :key="property.id"
                      @dblclick="handleDoubleClick($event, property)"
                      @click="selectRow($event, property, index)"
                      :class="{
                        'selected-row': selectedProperties.some(
                          (selected) =>
                            selected?.propertyId === property?.propertyId,
                        ), // Highlight selected rows based on propertyId
                        'odd-row': index % 2 === 1,
                        'even-row': index % 2 === 0,
                        'last-row': index === 0, // Add this line
                      }"
                    >
                      <td
                        :style="{
                          minWidth: '30px',
                          maxWidth: '30px',
                          textAlign: 'center',
                        }"
                      >
                        <i
                          v-if="property.expired === 1"
                          class="is-expired fas fa-circle-exclamation"
                          :title="'vacant or\nlease expired'"
                        ></i>
                        <span v-else>-</span>
                      </td>
                      <td
                        :title="'double click to jump to \nleases for this property'"
                        :style="{ minWidth: '120px', maxWidth: '120px' }"
                      >
                        {{ property.address }}
                      </td>
                      <td
                        class="specs-column"
                        :title="'double click to edit specs'"
                        :style="{ minWidth: '60px', maxWidth: '60px' }"
                      >
                        {{ property.specs }}
                      </td>
                      <td
                        :style="{
                          minWidth: '150px',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }"
                        :title="property.name + ' (' + property.leaseId + ')'"
                      >
                        {{ property.name }}
                      </td>
                      <td
                        :style="{
                          minWidth: '50px',
                          maxWidth: '50px',
                          textAlign: 'center',
                        }"
                        :class="{
                          'expiring-soon': isStartingSoon(property.startsOn),
                        }"
                      >
                        {{ property.startsOn }}
                      </td>
                      <td
                        :style="{
                          minWidth: '50px',
                          maxWidth: '50px',
                          textAlign: 'center',
                        }"
                        :title="
                          property.futureStartsOn === null
                            ? ''
                            : formatDate(property.futureStartsOn, false, true)
                        "
                        :class="{
                          'has-renewal': hasRenewal(property.futureStartsOn),
                          'expiring-soon': isExpiringSoon(property.endsOn),
                          'lease-is-expired': isExpired(property.endsOn),
                        }"
                      >
                        {{ property.endsOn }}
                      </td>
                      <td
                        class="tenant-emails"
                        :style="{
                          minWidth: '150px',
                          maxWidth: '150px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          fontWeight:
                            property.sameTenants === 1 ? 'bold' : 'normal',
                        }"
                        :title="
                          'Double-click: compose email to tenants\n' +
                          'CTRL+double-click: search emails only\n' +
                          'SHIFT+double-click: quick copy panel\n\n' +
                          property.tenants
                        "
                      >
                        {{ property.tenants }}
                      </td>
                      <td
                        :style="{
                          minWidth: '55px',
                          maxWidth: '55px',
                          textAlign: 'right',
                        }"
                      >
                        {{ formatNumber(property.monthlyBaseRent) }}
                      </td>
                      <td
                        class="rentchg-column"
                        :title="getRentChgCellTitle(property)"
                        :style="{
                          minWidth: '55px',
                          maxWidth: '55px',
                          textAlign: 'right',
                          padding: 0,
                          verticalAlign: 'middle',
                        }"
                      >
                        <div
                          v-if="hasRentChgValue(property.rentChgFuture)"
                          class="rentchg-cell-stack"
                        >
                          <div
                            v-if="hasRentChgValue(property.rentChg)"
                            class="rentchg-cell-line"
                            :style="
                              getRentChgLineStyle(property.rentChg, 'standard')
                            "
                          >
                            {{ getRentChgDisplayValue(property.rentChg) }}
                          </div>
                          <div
                            class="rentchg-cell-line"
                            :style="
                              getRentChgLineStyle(
                                property.rentChgFuture,
                                'future',
                              )
                            "
                          >
                            {{ getRentChgDisplayValue(property.rentChgFuture) }}
                          </div>
                        </div>
                        <div
                          v-else
                          class="rentchg-cell-line"
                          :style="
                            getRentChgLineStyle(property.rentChg, 'standard')
                          "
                        >
                          {{ getRentChgDisplayValue(property.rentChg) }}
                        </div>
                      </td>
                      <td
                        class="amount-column"
                        title="double click&#10;to search payments"
                        :style="{
                          minWidth: '65px',
                          maxWidth: '65px',
                          textAlign: 'right',
                        }"
                      >
                        {{ formatNumber(property.amount, 2) }}
                      </td>
                      <td
                        :style="{
                          minWidth: '65px',
                          maxWidth: '65px',
                          textAlign: 'right',
                        }"
                      >
                        {{ formatNumber(property.balance, 2) }}
                      </td>
                      <td
                        :style="{
                          minWidth: '30px',
                          maxWidth: '30px',
                          textAlign: 'center',
                          color:
                            property.balanceRatio <= 0 &&
                            property.balance < -100
                              ? 'red'
                              : 'inherit',
                          backgroundColor:
                            property.balanceRatio <= 0 &&
                            property.balance < -100
                              ? '#ffcccc'
                              : 'inherit',
                        }"
                      >
                        {{ displayRatio(property.balanceRatio) }}
                      </td>
                      <td
                        :style="{
                          minWidth: '50px',
                          maxWidth: '50px',
                          textAlign: 'center',
                        }"
                        :class="{
                          'payment-warning': property.aging > 45,
                          'payment-recent': property.aging < 15,
                        }"
                      >
                        {{ property.paidOn }}
                      </td>
                      <td
                        :style="{
                          minWidth: '30px',
                          maxWidth: '30px',
                          textAlign: 'center',
                        }"
                      >
                        {{ property.aging }}
                      </td>
                      <td
                        :title="
                          (property.leaseStateDescription ||
                            'click to view history; double click to set lease state') +
                          (property.leaseStateDate
                            ? '\n' + property.leaseStateDate
                            : '')
                        "
                        class="lease-state"
                        :style="{
                          minWidth: '40px',
                          maxWidth: '40px',
                          textAlign: 'center',
                          color: property.leaseStateForeground || '',
                          backgroundColor: property.leaseStateBackground || '',
                          cursor: 'pointer',
                        }"
                        @click.stop="
                          handleStateClick('lease', property.leaseId, $event)
                        "
                      >
                        {{ property.leaseState }}
                      </td>
                      <td
                        :title="
                          property.propertyStateDescription ||
                          'click to view history; double click to set property state'
                        "
                        class="property-state"
                        :style="{
                          minWidth: '40px',
                          maxWidth: '40px',
                          textAlign: 'center',
                          color: property.propertyStateForeground || '',
                          backgroundColor:
                            property.propertyStateBackground || '',
                          cursor: 'pointer',
                        }"
                        @click.stop="
                          handleStateClick(
                            'property',
                            property.propertyId,
                            $event,
                          )
                        "
                      >
                        {{ property.propertyState }}
                      </td>
                      <td
                        class="notes-column"
                        :title="'click to show;\ndouble click to edit'"
                        :style="{
                          minWidth: '30px',
                          maxWidth: '30px',
                          textAlign: 'center',
                        }"
                        @click.stop="toggleTooltip($event, property)"
                        style="cursor: pointer"
                      >
                        <span
                          class="notes-column"
                          v-if="property.noteCount > 0"
                        >
                          <i class="notes-column fas fa-sticky-note"></i>
                        </span>
                        <span class="notes-column" v-else>-</span>
                      </td>
                      <td
                        :style="{
                          minWidth: '24px',
                          maxWidth: '24px',
                          textAlign: 'center',
                          padding: '0',
                        }"
                        :title="petTooltip(property)"
                      >
                        <i
                          v-if="property.dogCount > 0"
                          class="fas fa-dog"
                          :style="{
                            color: temperamentColor(property.dogTemperament),
                            fontSize: '0.8em',
                          }"
                        ></i>
                        <i
                          v-if="property.catCount > 0"
                          class="fas fa-cat"
                          :style="{
                            color: temperamentColor(property.catTemperament),
                            fontSize: '0.8em',
                            marginLeft: property.dogCount > 0 ? '1px' : '0',
                          }"
                        ></i>
                        <i
                          v-if="
                            property.petCount > 0 &&
                            !property.dogCount &&
                            !property.catCount &&
                            !property.dogTemperament &&
                            !property.catTemperament
                          "
                          class="fas fa-paw"
                          :style="{
                            color: '#ccc',
                            fontSize: '0.8em',
                          }"
                        ></i>
                      </td>
                      <td
                        class="toggle-check"
                        :style="{
                          minWidth: '30px',
                          maxWidth: '30px',
                          textAlign: 'center',
                        }"
                        :title="pixieAccessTooltip(property)"
                      >
                        <i
                          v-if="property.pixieLastAccess"
                          class="fas fa-mobile-alt pixie-access-icon"
                          :class="{
                            'pixie-access-today': isPixieAccessToday(
                              property.pixieLastAccess,
                            ),
                          }"
                        ></i>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="payment-stats">
            <div class="email-multiple">
              <button @click="openEmailEditor($event)">
                <i class="fas fa-envelope"></i>
              </button>
              <span style="margin-left: 10px; font-size: 0.85em; color: #888"
                >Demand letters email to Dan</span
              >
              <button
                style="margin-left: 8px"
                @click="recalcRentChg"
                title="Recalculate Chg from current vs previous lease (rent + parking)"
              >
                <i class="fas fa-calculator"></i>
              </button>
              <span style="margin-left: 10px; font-size: 0.85em; color: #888"
                >recalc chg</span
              >
            </div>
            <label>payments received: -</label>
            <input v-model="daysBefore" ref="daysBeforeInput" type="number" />
            <label> days to +</label>
            <input v-model="daysAfter" ref="daysAfterInput" type="number" />
            <label> days after the 1st: </label>
            <span>{{ paymentCount }}</span>
            <span>
              of {{ filteredPropertiesTotal.length - 1 }} =
              {{ (paymentPercent * 100.0).toFixed(1) }}% (
            </span>
            <span> {{ formattedPaymentsTotal }} )</span>
            <button style="margin-left: 5px" @click="showChart = !showChart">
              <i class="fas fa-chart-column"></i>
            </button>
          </div>
        </div>

        <!-- Tab 2: Leases by Month -->
        <div id="leasesByMonth" v-if="activeTab === 'leasesByMonth'">
          <div class="search-input-wrapper">
            <!-- Search bar for Leases by Month -->
            <div class="report-title">
              {{ chartOptions[selectedChart].title }}
            </div>
            <div class="search-bar">
              <div class="search-left">
                <div class="label">Search Address</div>
                <input
                  name="dontSuggestMe"
                  ref="leaseSearchQuery"
                  v-model="leaseSearchQuery"
                  @input="onLeaseSearch"
                  @keydown.esc="
                    if (!leaseSearchQuery) $refs.leaseSearchQuery.blur();
                  "
                  type="text"
                  placeholder="Search for address"
                  class="search-input"
                  autocomplete="new-password"
                />
                <button @click="clearLeaseSearch" class="clear-button">
                  X
                </button>
                <div class="year-select">
                  <span class="label">Select Year </span>
                  <select
                    style="
                      border: 1px solid #aaa;
                      border-radius: 4px;
                      height: 24px;
                    "
                    v-model="yearToShow"
                  >
                    <option v-for="year in years" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </div>
                <button
                  style="margin-left: 5px"
                  @click="printElement('leasesByMonth', 'print')"
                >
                  <i class="fas fa-print"></i>
                </button>
              </div>

              <div class="search-right">
                <div class="toggle-mode">
                  <button @click="refreshGrid">
                    <i style="margin-right: 15px" class="fas fa-refresh"></i>
                  </button>
                  <button style="width: 80px" @click="switchGrid">
                    {{ chartOptions[selectedChart].buttonLabel }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- New table for Leases by Month -->
          <div class="table-header" ref="dataGridHeader">
            <table>
              <thead>
                <tr>
                  <th style="min-width: 150px; max-width: 150px">
                    Address
                    <div
                      v-if="chartOptions[selectedChart].buttonLabel === 'Lease'"
                      class="monthly-summary"
                    >
                      vacancy Mos: {{ totalVacancyMonths }}
                    </div>
                  </th>
                  <th
                    v-for="month in months"
                    :key="month"
                    style="text-align: center; min-width: 80px; max-width: 80px"
                  >
                    {{ month }}
                    <div class="monthly-summary">
                      <!-- Lease case -->
                      <span
                        v-if="
                          chartOptions[selectedChart].buttonLabel === 'Lease'
                        "
                      >
                        {{ monthlySummary[month]?.leaseCount || 0 }}
                        leases<br />
                        ${{
                          formatNumber(monthlySummary[month]?.totalRent || 0)
                        }}
                      </span>
                      <!-- Income case -->
                      <span
                        v-if="
                          chartOptions[selectedChart].buttonLabel === 'Income'
                        "
                      >
                        ${{
                          formatNumber(monthlySummary[month]?.toFixed(0) || 0)
                        }}
                      </span>
                      <!-- Balance case -->
                      <span
                        v-if="
                          chartOptions[selectedChart].buttonLabel === 'Balance'
                        "
                      >
                        {{
                          !(months.indexOf(month) > new Date().getMonth())
                            ? formatNumber(
                                monthlySummary[month]?.toFixed(0) || 0,
                              )
                            : '-'
                        }}
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
            </table>
          </div>
          <div class="table-body-wrapper">
            <div class="table-body" ref="dataGridBody">
              <table>
                <tbody>
                  <tr
                    v-for="(property, index) in filteredLeaseCalendar"
                    :key="property.address"
                    @dblclick="navigateToLeaseSearch(property)"
                    @click="selectRow($event, property, index)"
                    :class="{
                      'odd-row': index % 2 === 1,
                      'even-row': index % 2 === 0,
                      // 'selected-row': selectedProperty && selectedProperty.address === property.address
                      'selected-row': selectedProperties.some(
                        (selected) =>
                          selected.propertyId === property.propertyId,
                      ), // Highlight selected rows based on propertyId
                    }"
                  >
                    <td
                      style="
                        font-weight: bold;
                        min-width: 150px;
                        max-width: 150px;
                      "
                    >
                      {{ property.address }}
                    </td>
                    <td
                      v-for="month in months"
                      :key="month"
                      :title="'click to search\npayments for this month'"
                      style="
                        text-align: center;
                        font-family: monospace;
                        min-width: 80px;
                        max-width: 80px;
                      "
                      @click="navigateToPayments(month, property)"
                    >
                      <span
                        v-if="shouldDisplayProperty(property, month)"
                        v-html="property[month]"
                      ></span>
                      <span v-else>{{ elseDisplayValue(month) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <!-- Tab 3: PaymentViewer -->
        <div style="margin-left: -25px" v-if="activeTab === 'payments'">
          <PaymentViewer />
        </div>
        <!-- Tab 5: Early Resigns -->
        <div v-if="activeTab === 'resigns'" class="resigns-tab">
          <div class="resigns-toolbar">
            <input
              ref="resignFilterInput"
              v-model="resignFilter"
              type="text"
              placeholder="Filter by address or tenant... (ESC clears)"
              class="resign-filter-input"
              @keydown.esc="
                if (!resignFilter && resignEndDateFilterIndex === -1) {
                  $refs.resignFilterInput.blur();
                } else {
                  resignFilter = '';
                  resignSortField = '';
                  resignSortOrder = 'asc';
                  resignEndDateFilterIndex = -1;
                  endDateFilterLabel = '';
                  resignUniqueEndDates = [];
                }
              "
            />
            <button
              @click="editSelectedResign"
              class="edit-btn"
              title="Edit selected row (or press 'e')"
              :disabled="!selectedResignId"
            >
              <i class="fas fa-edit"></i>
            </button>
            <button @click="fetchResigns" class="refresh-btn" title="Refresh">
              <i class="fas fa-sync-alt"></i>
            </button>
            <button @click="printResigns" class="print-btn" title="Print">
              <i class="fas fa-print"></i>
            </button>
            <button
              @click="exportResignsExcel"
              class="excel-btn"
              title="Export to Excel"
            >
              <i class="fas fa-file-excel"></i>
            </button>
            <button
              @click="cycleEndDateFilter"
              class="excel-btn"
              title="Cycle by end date"
              style="position: relative"
            >
              <i class="fas fa-calendar-day"></i>
              <span
                v-if="endDateFilterLabel"
                style="
                  position: absolute;
                  bottom: -12px;
                  left: 50%;
                  transform: translateX(-50%);
                  font-size: 9px;
                  color: #333;
                  font-weight: bold;
                  white-space: nowrap;
                "
                >{{ endDateFilterLabel }}</span
              >
            </button>
            <button
              @click="showLeaseEndChart = true"
              class="excel-btn"
              title="Lease end date distribution chart"
            >
              <i class="fas fa-chart-bar"></i>
            </button>
            <span
              v-if="selectedResignId && showResignEditHint"
              class="resign-edit-hint"
            >
              press 'e' key to edit
            </span>
          </div>
          <div
            class="resigns-table-wrapper"
            @mouseenter="resignTableHovered = true"
            @mouseleave="resignTableHovered = false"
          >
            <table class="resigns-table">
              <thead>
                <tr>
                  <th
                    style="width: 30px"
                    class="sortable-th"
                    @click="sortResignsBy('avail')"
                  >
                    Avl
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'avail',
                        'fa-sort-up':
                          resignSortField === 'avail' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'avail' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th style="width: 180px">Addr. (dbl-clickable)</th>
                  <th style="width: 140px">Tenants (dbl-click: email)</th>
                  <th
                    style="width: 60px"
                    class="sortable-th"
                    @click="sortResignsBy('endsOnRaw')"
                  >
                    Ends
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'endsOnRaw',
                        'fa-sort-up':
                          resignSortField === 'endsOnRaw' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'endsOnRaw' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 50px"
                    class="sortable-th"
                    @click="sortResignsBy('currentRent')"
                  >
                    Rent
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'currentRent',
                        'fa-sort-up':
                          resignSortField === 'currentRent' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'currentRent' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 40px"
                    class="sortable-th"
                    @click="sortResignsBy('pet')"
                  >
                    Pet
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'pet',
                        'fa-sort-up':
                          resignSortField === 'pet' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'pet' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 45px"
                    class="sortable-th"
                    @click="sortResignsBy('offer')"
                  >
                    Offer
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'offer',
                        'fa-sort-up':
                          resignSortField === 'offer' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'offer' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 50px; text-align: right"
                    class="sortable-th"
                    @click="sortResignsBy('newRent')"
                  >
                    New
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'newRent',
                        'fa-sort-up':
                          resignSortField === 'newRent' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'newRent' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 35px; text-align: center"
                    class="sortable-th"
                    title="Include Trash in lease"
                    @click="sortResignsBy('inclTrash')"
                  >
                    Trsh
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'inclTrash',
                        'fa-sort-up':
                          resignSortField === 'inclTrash' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'inclTrash' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th style="width: 40px; text-align: right" title="Trash Fee">
                    Fee
                  </th>
                  <th
                    style="width: 90px; text-align: center"
                    class="sortable-th"
                    @click="sortResignsBy('lastContactedRaw')"
                  >
                    Contacted
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'lastContactedRaw',
                        'fa-sort-up':
                          resignSortField === 'lastContactedRaw' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'lastContactedRaw' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 30px; text-align: center"
                    class="sortable-th"
                    @click="sortResignsBy('modifiedOnRaw')"
                    title="Sort by last modified"
                  >
                    <i class="fas fa-clock" style="color: #ccc"></i>
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'modifiedOnRaw',
                        'fa-sort-up':
                          resignSortField === 'modifiedOnRaw' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'modifiedOnRaw' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th
                    style="width: 110px; text-align: center"
                    class="sortable-th"
                    @click="sortResignsBy('status')"
                  >
                    Status
                    <i
                      class="fas"
                      :class="{
                        'fa-sort': resignSortField !== 'status',
                        'fa-sort-up':
                          resignSortField === 'status' &&
                          resignSortOrder === 'asc',
                        'fa-sort-down':
                          resignSortField === 'status' &&
                          resignSortOrder === 'desc',
                      }"
                    ></i>
                  </th>
                  <th style="width: 35px">LStat</th>
                  <th style="width: 35px">PStat</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, index) in filteredResigns"
                  :key="row.propertyId"
                  @click="selectedResignId = row.propertyId"
                  @dblclick="handleResignDoubleClick($event, row)"
                  :class="{
                    'resign-completed': row.status === 'completed',
                    'selected-row': selectedResignId === row.propertyId,
                    'odd-row': index % 2 === 1,
                    'even-row': index % 2 === 0,
                  }"
                >
                  <td
                    style="text-align: center; cursor: pointer"
                    @click.stop="toggleResignAvail(row)"
                  >
                    <i
                      v-if="row.avail === 1"
                      class="fas fa-check"
                      style="color: green"
                    ></i>
                    <i v-else class="fas fa-times" style="color: red"></i>
                  </td>
                  <td
                    class="resign-address-cell"
                    title="Double-click to open Lease Search"
                    v-html="highlightResignSearch(row.address)"
                  ></td>
                  <td
                    class="resign-tenants-cell"
                    :style="{
                      fontWeight: row.sameTenants === 1 ? 'bold' : 'normal',
                    }"
                    :title="
                      'Double-click: compose email to tenants\n' +
                      'CTRL+double-click: search emails only\n' +
                      'SHIFT+double-click: quick copy panel\n\n' +
                      row.tenants
                    "
                    v-html="highlightResignSearch(row.tenants)"
                  ></td>
                  <td>{{ row.endsOn }}</td>
                  <td style="text-align: right">
                    {{ formatNumber(row.currentRent) }}
                  </td>
                  <td
                    style="text-align: right"
                    :style="{
                      backgroundColor:
                        row.pet != null && row.pet !== ''
                          ? '#d4edda'
                          : undefined,
                      color:
                        row.pet != null && row.pet !== ''
                          ? '#155724'
                          : undefined,
                    }"
                  >
                    {{
                      row.pet != null && row.pet !== ''
                        ? formatNumber(row.pet)
                        : ''
                    }}
                  </td>
                  <td
                    style="text-align: right"
                    :style="{
                      backgroundColor:
                        row.resignDiscountCredit != null &&
                        parseFloat(row.resignDiscountCredit) ===
                          parseFloat(row.offer)
                          ? '#d4edda'
                          : row.resignDiscountCredit != null
                            ? '#fff3cd'
                            : undefined,
                      color:
                        row.resignDiscountCredit != null &&
                        parseFloat(row.resignDiscountCredit) ===
                          parseFloat(row.offer)
                          ? '#155724'
                          : row.resignDiscountCredit != null
                            ? '#856404'
                            : undefined,
                    }"
                    :title="
                      row.resignDiscountCredit != null
                        ? 'Ledger discount: ' +
                          formatNumber(row.resignDiscountCredit)
                        : ''
                    "
                  >
                    {{ formatNumber(row.offer) }}
                  </td>
                  <td
                    style="text-align: right"
                    :style="{
                      backgroundColor: getNewRentBackground(
                        row.newRent,
                        row.currentRent,
                        row.trashFee,
                      ),
                      color: getNewRentColor(
                        row.newRent,
                        row.currentRent,
                        row.trashFee,
                      ),
                    }"
                  >
                    {{ row.newRent ? formatNumber(row.newRent) : '' }}
                  </td>
                  <td
                    style="text-align: center; cursor: pointer"
                    @click.stop="toggleResignInclTrash(row)"
                  >
                    <i
                      v-if="row.inclTrash === 1"
                      class="fas fa-check"
                      style="color: green"
                    ></i>
                    <i v-else class="fas fa-times" style="color: red"></i>
                  </td>
                  <td style="text-align: right">
                    {{
                      row.trashFee && parseFloat(row.trashFee) !== 0
                        ? formatNumber(row.trashFee)
                        : ''
                    }}
                  </td>
                  <td style="text-align: center; min-width: 80px">
                    {{ row.lastContacted }}
                  </td>
                  <td
                    style="text-align: center; width: 30px"
                    :title="
                      row.modifiedOnRaw
                        ? new Date(row.modifiedOnRaw).toLocaleString() +
                          (row.modifiedByUsername
                            ? ' by ' + row.modifiedByUsername
                            : '')
                        : ''
                    "
                  >
                    <i
                      v-if="row.modifiedOnRaw"
                      class="fas fa-clock"
                      :style="{ color: index % 2 === 1 ? '#ddd' : '#bbb' }"
                    ></i>
                  </td>
                  <td
                    class="resign-status-cell"
                    :class="
                      row.status
                        ? 'status-' + row.status.replace(/\s+/g, '-')
                        : ''
                    "
                    v-html="highlightResignSearch(row.status)"
                  ></td>
                  <td
                    :title="
                      (row.leaseStateDescription || '') +
                      (row.leaseStateDate ? '\n' + row.leaseStateDate : '')
                    "
                    :style="{
                      textAlign: 'center',
                      color: row.leaseStateForeground || '',
                      backgroundColor: row.leaseStateBackground || '',
                    }"
                    v-html="highlightResignSearch(row.leaseState)"
                  ></td>
                  <td
                    :title="row.propertyStateDescription"
                    :style="{
                      textAlign: 'center',
                      color: row.propertyStateForeground || '',
                      backgroundColor: row.propertyStateBackground || '',
                    }"
                    v-html="highlightResignSearch(row.propertyState)"
                  ></td>
                  <td
                    class="note-cell"
                    :title="row.note"
                    v-html="highlightResignSearch(row.note)"
                  ></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="resigns-summary">
            Total: {{ filteredResigns.length }} properties | Available:
            {{ filteredResigns.filter((r) => r.avail === 1).length }} | In
            Progress:
            {{
              filteredResigns.filter(
                (r) =>
                  r.status === 'docusign needed' ||
                  r.status === 'resigning in progress',
              ).length
            }}
            ({{
              filteredResigns.filter((r) => r.avail === 1).length > 0
                ? (
                    (filteredResigns.filter(
                      (r) =>
                        r.status === 'docusign needed' ||
                        r.status === 'resigning in progress',
                    ).length /
                      filteredResigns.filter((r) => r.avail === 1).length) *
                    100
                  ).toFixed(0)
                : 0
            }}%;
            {{
              filteredResigns.length > 0
                ? (
                    (filteredResigns.filter(
                      (r) =>
                        r.status === 'docusign needed' ||
                        r.status === 'resigning in progress',
                    ).length /
                      filteredResigns.length) *
                    100
                  ).toFixed(0)
                : 0
            }}%) | Lease Created:
            {{
              filteredResigns.filter((r) => r.status === 'lease created').length
            }}
            | Annual Rent Δ:
            {{
              formatNumber(
                filteredResigns.reduce((sum, r) => {
                  if (r.newRent && r.currentRent) {
                    return sum + (r.newRent - r.currentRent) * 12;
                  }
                  return sum;
                }, 0),
              )
            }}
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeTab === 'overview'">
      <div class="strip-chart-wrapper">
        <div
          v-for="property in sortedPropertyData"
          :key="property.propertyId"
          :title="`${property.address}\n${property.tenants || ''}\n${
            property.amount || ''
          } ${property.paidOn || ''}\nbal: ${property.balance | ''}`"
          :style="{
            backgroundColor: getSquareColor(property),
            border:
              property.balance < 0 ? '1px solid #555' : '1px solid #00bb00',
          }"
          class="property-square"
          @dblclick="openLeaseSearch(property)"
        >
          <i
            style="
              position: relative;
              font-size: 0.5em;
              color: rgb(95 92 92);
              top: -7px;
            "
            v-if="property.earlyResign === 1"
            class="fas fa-add"
          ></i>
        </div>
      </div>
    </div>

    <div
      v-if="showCopyPanel"
      ref="copyPanel"
      class="copy-panel"
      @dblclick="showCopyPanel = false"
    >
      <div class="copy-panel-header">
        Click addr, name or email to copy to clipboard
      </div>
      <div
        style="margin-top: 5px; cursor: pointer"
        @click="copyToClipboard(propertyToCopy)"
        class
      >
        {{ propertyToCopy }}
      </div>
      <div
        style="margin-top: 5px; cursor: pointer"
        @click="copyToClipboard(rentToCopy)"
        class="copy-panel-item"
      >
        {{ rentToCopy }}
      </div>
      <!-- Display tenants -->
      <div style="margin-top: 5px">
        <label>Tenants:</label>
        <div v-for="(tenant, index) in tenantsToCopy" :key="index">
          <span
            class="copy-panel-item"
            @click="copyToClipboard(tenant)"
            style="cursor: pointer"
          >
            {{ tenant }}
          </span>
        </div>
      </div>

      <!-- Display emails -->
      <div style="margin-top: 5px">
        <label>Emails:</label>
        <div v-for="(email, index) in emailsToCopy" :key="index">
          <span
            class="copy-panel-item"
            @click="copyToClipboard(email)"
            style="cursor: pointer"
          >
            {{ email }}
          </span>
        </div>
      </div>
      <!-- Display phones -->
      <div style="margin-top: 5px">
        <label>Phones:</label>
        <div v-for="(phone, index) in phonesToCopy" :key="index">
          <span
            class="copy-panel-item"
            @click="copyToClipboard(phone)"
            style="cursor: pointer"
          >
            {{ phone }}
          </span>
        </div>
      </div>

      <div class="mini-calc">
        <small>quick calc: </small>
        <input
          v-model="calcExpression"
          @input="doCalc"
          placeholder="ex: 12*1234.50"
        />
        <span
          style="margin-left: 10px"
          @click="copyToClipboard(calcResult)"
          class="calc-result"
          >{{ calcResult }}</span
        >
      </div>

      <!-- Display message when copied -->
      <div v-show="copyMessage" class="copy-message">
        {{ copyMessage }}
      </div>

      <div style="margin-top: 8px"><small>double-click to close</small></div>
    </div>

    <!-- Specs Edit Dialog -->
    <div v-if="showSpecsDialog" ref="specsDialog" class="specs-dialog">
      <div class="specs-dialog-header">
        Edit Specs for {{ selectedProperty?.address }}
      </div>
      <div class="specs-dialog-content">
        <label for="specsSelect">Select existing specs:</label>
        <select id="specsSelect" v-model="selectedSpecs" class="specs-select">
          <option value="">-- Select --</option>
          <option v-for="spec in availableSpecs" :key="spec" :value="spec">
            {{ spec }}
          </option>
          <option value="NEW">New...</option>
        </select>

        <div v-if="selectedSpecs === 'NEW'" class="new-specs-input">
          <label for="newSpecs">Enter new specs:</label>
          <input
            id="newSpecs"
            v-model="newSpecs"
            type="text"
            placeholder="e.g., 2B1.5ba"
            class="specs-input"
          />
        </div>

        <div class="specs-dialog-buttons">
          <button @click="saveSpecs" class="save-button">Save</button>
          <button @click="closeSpecsDialog" class="close-button">Close</button>
        </div>
      </div>
    </div>

    <!-- RentChg Edit Dialog -->
    <div v-if="showRentChgDialog" ref="rentChgDialog" class="rentchg-dialog">
      <div class="rentchg-dialog-header">
        Edit Rent Change for {{ selectedProperty?.address }}
      </div>
      <div class="rentchg-dialog-content">
        <label for="rentChgSelect">Select existing rent change:</label>
        <select
          id="rentChgSelect"
          v-model="selectedRentChg"
          class="rentchg-select"
        >
          <option value="">-- Select --</option>
          <option
            v-for="rentChg in availableRentChg"
            :key="rentChg"
            :value="rentChg"
          >
            {{ rentChg }}
          </option>
          <option value="NEW">New...</option>
        </select>

        <div v-if="selectedRentChg === 'NEW'" class="new-rentchg-input">
          <label for="newRentChg">Enter new rent change:</label>
          <input
            id="newRentChg"
            v-model="newRentChg"
            type="number"
            step="0.01"
            placeholder="e.g., 50.00 or 0.00"
            class="rentchg-input"
          />
        </div>

        <div class="rentchg-dialog-buttons">
          <button @click="saveRentChg" class="save-button">Save</button>
          <button @click="closeRentChgDialog" class="close-button">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- State History Dialog -->
    <div
      v-if="showStateHistoryDialog"
      ref="stateHistoryDialog"
      class="state-history-dialog"
    >
      <div class="state-history-dialog-header">
        {{ stateHistoryType === 'lease' ? 'Lease' : 'Property' }} State History
      </div>
      <div class="state-history-dialog-content">
        <div class="state-history-columns">
          <!-- Lease States Column -->
          <div class="state-column">
            <h4>Lease States</h4>
            <div class="state-table">
              <div class="state-table-header">
                <span>Date</span>
                <span>Code</span>
                <span>Description</span>
              </div>
              <div
                v-for="state in stateHistoryData.lease"
                :key="state.date + state.shortCode"
                class="state-table-row"
              >
                <span>{{ formatDate(state.date, false, true) }}</span>
                <span>{{ state.shortCode }}</span>
                <span>{{ state.description }}</span>
              </div>
              <div v-if="stateHistoryData.lease.length === 0" class="no-data">
                No lease state history
              </div>
            </div>
          </div>

          <!-- Property States Column -->
          <div class="state-column">
            <h4>Property States</h4>
            <div class="state-table">
              <div class="state-table-header">
                <span>Date</span>
                <span>Code</span>
                <span>Description</span>
              </div>
              <div
                v-for="state in stateHistoryData.property"
                :key="state.date + state.shortCode"
                class="state-table-row"
              >
                <span>{{ formatDate(state.date, false, true) }}</span>
                <span>{{ state.shortCode }}</span>
                <span>{{ state.description }}</span>
              </div>
              <div
                v-if="stateHistoryData.property.length === 0"
                class="no-data"
              >
                No property state history
              </div>
            </div>
          </div>
        </div>

        <div class="state-history-dialog-buttons">
          <button @click="closeStateHistoryDialog" class="close-button">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Lease End Distribution Chart -->
    <div
      v-if="showLeaseEndChart"
      class="resign-dialog-overlay"
      style="align-items: center; justify-content: center"
      @click.self="showLeaseEndChart = false"
    >
      <LeaseEndDistribution @close="showLeaseEndChart = false" />
    </div>

    <!-- Resign Edit Dialog -->
    <div
      v-if="showResignDialog"
      class="resign-dialog-overlay"
      @click.self="showResignDialog = false"
    >
      <div class="resign-dialog" @keydown.stop>
        <div class="resign-dialog-header">
          Edit Resign Tracking for {{ editingResign?.address }}
        </div>
        <div class="resign-dialog-content">
          <div class="resign-dialog-row">
            <label>Tenants:</label>
            <span>{{ editingResign?.tenants }}</span>
          </div>
          <div class="resign-dialog-row">
            <label>Lease Ends:</label>
            <span>{{ editingResign?.endsOn }}</span>
          </div>
          <div class="resign-dialog-row">
            <label>Current Rent:</label>
            <span>{{ formatNumber(editingResign?.currentRent) }}</span>
          </div>
          <div class="resign-dialog-row">
            <label for="resignAvail">Available:</label>
            <input
              id="resignAvail"
              type="checkbox"
              v-model="editingResign.avail"
              :true-value="1"
              :false-value="0"
            />
          </div>
          <div class="resign-dialog-flex-row" style="display: flex; gap: 12px">
            <div style="flex: 1; display: flex; flex-direction: column">
              <label
                for="resignOffer"
                style="font-size: 11px; font-weight: bold; padding-bottom: 3px"
                >Offer ($)</label
              >
              <input
                id="resignOffer"
                type="number"
                step="25"
                v-model="editingResign.offer"
                class="resign-input"
                style="width: 50%"
              />
            </div>
            <div style="flex: 1; display: flex; flex-direction: column">
              <label
                for="resignNewRent"
                style="font-size: 11px; font-weight: bold; padding-bottom: 3px"
                >New Rent ($)</label
              >
              <input
                id="resignNewRent"
                type="number"
                step="25"
                v-model="editingResign.newRent"
                class="resign-input"
                style="width: 50%"
              />
            </div>
            <div style="flex: 1; display: flex; flex-direction: column">
              <label
                for="resignPet"
                style="font-size: 11px; font-weight: bold; padding-bottom: 3px"
                >Pet ($)</label
              >
              <input
                id="resignPet"
                type="number"
                step="25"
                v-model="editingResign.pet"
                class="resign-input"
                style="width: 50%"
              />
            </div>
          </div>
          <div class="resign-dialog-row">
            <label for="resignInclTrash">Include Trash:</label>
            <input
              id="resignInclTrash"
              type="checkbox"
              v-model="editingResign.inclTrash"
              :true-value="1"
              :false-value="0"
            />
          </div>
          <div class="resign-dialog-row">
            <label for="resignTrashFee">Trash Fee ($):</label>
            <input
              id="resignTrashFee"
              type="number"
              step="5"
              v-model="editingResign.trashFee"
              class="resign-input"
            />
          </div>
          <div class="resign-dialog-row">
            <label for="resignContacted">Last Contacted:</label>
            <input
              id="resignContacted"
              type="date"
              v-model="editingResign.lastContactedRaw"
              class="resign-input"
            />
          </div>
          <div class="resign-dialog-row">
            <label for="resignStatus">Status:</label>
            <select
              id="resignStatus"
              v-model="editingResign.status"
              class="resign-select"
            >
              <option value="">-- None --</option>
              <option value="docusign needed">Docusign Needed</option>
              <option value="resigning in progress">
                Resigning in Progress
              </option>
              <option value="completed">Completed Docusign</option>
              <option value="lease downloaded">Lease Downloaded</option>
              <option value="document uploaded">Document Uploaded</option>
              <option value="lease created">Lease Created</option>
            </select>
          </div>
          <div class="resign-dialog-row">
            <label for="resignNote">Note:</label>
            <textarea
              id="resignNote"
              v-model="editingResign.note"
              class="resign-textarea"
              rows="3"
            ></textarea>
          </div>
          <div class="resign-dialog-buttons">
            <button @click="saveResign" class="save-button">Save</button>
            <button @click="showResignDialog = false" class="close-button">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Importing NotesPanel -->
    <NotesPanel
      :showNotesPanel="this.showNotesPanel"
      :noteType="this.noteType"
      :noteId="this.noteId"
      @update-show-notes-panel="updateShowNotesPanel"
    />

    <LeaseState
      :leaseId="selectedLeaseId"
      :propertyId="selectedPropertyId"
      v-model:showStatesDialog="showStatesDialog"
      @close="closeLeaseStateDialog"
    />
  </div>

  <div v-if="showChart" class="chart-container" @click="showChart = false">
    <!-- Embed the PlotReceipts component -->
    <PlotReceipts :barChartData="chartData ? chartData : []" />
  </div>

  <div
    v-if="pdfUrl && showPDF"
    class="attachment-viewer draggable-popup"
    ref="pdfViewer"
  >
    <div class="attachment-viewer-header">
      <div class="attachment-viewer-title">
        {{ viewerTitle }}
        <i
          class="fas fa-crosshairs draggable-icon"
          :class="{ active: isDragMode }"
          ref="ledgerListPopupIcon"
          @click.stop="toggleDragMode"
        ></i>
      </div>
      <button class="attachment-viewer-close-button" @click="showPDF = false">
        X
      </button>
    </div>
    <iframe
      id="pdf-frame"
      :src="pdfUrl"
      width="100%"
      height="100%"
      frameborder="0"
    ></iframe>
  </div>
  <!-- Tooltip Element -->
  <div v-if="activeTooltip" ref="tooltip" class="custom-tooltip">
    <div
      style="margin-bottom: 5px"
      @click="
        activeTooltip = false;
        handleNotesColumnClick(this.selectedProperty);
      "
    >
      <span>most recent note:</span
      ><i
        title="open notes editor"
        style="position: absolute; right: 5px"
        class="fas fa-external-link-alt"
      ></i>
    </div>
    <span @click="hideTooltip" v-html="tooltipContent"></span>
  </div>

  <!-- Markdown copy toast notification -->
  <div v-if="showMarkdownToast" class="markdown-toast">
    <i class="fas fa-check-circle"></i> Markdown copied to clipboard
  </div>

  <!-- confirmation popup -->
  <div v-if="showDialogPopup" class="confirmation-popup-content">
    <div class="confirmation-popup-title">Confirmation needed</div>
    <div v-html="dialogMessage"></div>
    <div class="confirmation-popup-buttons">
      <button
        v-for="option in dialogOptions"
        :key="option"
        @click="onDialogOptionSelected(option)"
        :id="option === 'Confirm' ? 'confirmButton' : ''"
      >
        {{ option }}
      </button>
    </div>
  </div>
  <!-- Lease Search iframe overlay (resign tab) -->
  <div
    v-if="resignIframeUrl"
    class="resign-iframe-overlay"
    @click.self="resignIframeUrl = null"
  >
    <div class="resign-iframe-container">
      <div class="resign-iframe-title-bar">
        <span>{{ resignIframeTitle }}</span>
        <button class="resign-iframe-close-btn" @click="resignIframeUrl = null">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="resign-iframe-scroll-wrapper">
        <div v-if="resignIframeLoading" class="resign-iframe-spinner-overlay">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading lease…</span>
        </div>
        <iframe
          :src="resignIframeUrl"
          class="resign-lease-iframe"
          @load="resignIframeLoading = false"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig'; // Adjust based on your axios configuration
import eventBus from './eventBus.js';
import NotesPanel from './NotesPanel.vue';
import PaymentViewer from './PaymentViewer.vue';
import PlotReceipts from './PlotReceipts.vue'; // Adjust the path as needed
import LeaseState from './LeaseState.vue';
import LeaseEndDistribution from './LeaseEndDistribution.vue';
import { marked } from 'marked';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import io from 'socket.io-client';
import { formatPixieNotifications } from '@/utils/pixieAccessTooltip';

export default {
  components: {
    NotesPanel,
    LeaseState,
    LeaseEndDistribution,
    PaymentViewer,
    PlotReceipts,
  },

  created() {
    this.updatePageTitle();
    this.getProperties();
    // Fetch properties and wait for completion
  },

  data() {
    const currentYear = new Date().getFullYear();
    return {
      // for lease endings year filter
      leaseEndingsYear: currentYear,
      // for popup
      copyMessage: '',
      calcResult: 0,
      propertyToCopy: '',
      rentToCopy: 0,
      tenantsToCopy: [],
      emailsToCopy: [],
      phonesToCopy: [],
      showCopyPanel: false,
      dialogMessage: '',
      dialogOptions: [],
      showDialogPopup: false,
      showMarkdownToast: false,

      // for specs popup
      showSpecsDialog: false,
      selectedProperty: null,
      selectedSpecs: '',
      newSpecs: '',
      availableSpecs: [],

      // for rentChg popup
      showRentChgDialog: false,
      selectedRentChg: '',
      newRentChg: '',
      availableRentChg: [],

      // for state history popup
      showStateHistoryDialog: false,
      stateHistoryData: {
        lease: [],
        property: [],
      },
      stateHistoryType: '', // 'lease' or 'property'
      stateHistoryId: null,

      // for tooltips
      tooltipContent: '',
      activeTooltip: false,
      showStatesDialog: false,
      selectedLeaseId: null,
      selectedPropertyId: null,
      activeTab: 'overview',
      clickTimeout: null,
      searchQuery: '',
      expirationFilterState: '', // Tracks cycling states: '', ':ex30', ':ex60', ':exAll'
      propertyData: [],
      showEditPropertyDialog: false,
      isEditing: false,
      propertyTableHeaders: [
        {
          label: 'Exp.',
          field: 'expired',
          width: '30px',
          align: 'center',
          type: 'number',
        },
        {
          label: 'Address',
          field: 'address',
          width: '120px',
          align: 'left',
          type: 'string',
        },
        {
          label: 'Specs',
          field: 'specs',
          width: '60px',
          align: 'left',
          type: 'string',
        },
        {
          label: 'Lease Name',
          field: 'name',
          width: '150px',
          align: 'left',
          type: 'string',
        },
        {
          label: 'Starts',
          field: 'startsOn',
          width: '50px',
          align: 'center',
          type: 'date',
        },
        {
          label: 'Ends',
          field: 'endsOn',
          width: '50px',
          align: 'center',
          type: 'date',
        },
        {
          label: 'Tenants',
          field: 'tenants',
          width: '150px',
          align: 'left',
          type: 'string',
        },
        {
          label: 'Rent',
          field: 'monthlyBaseRent',
          width: '55px',
          align: 'right',
          type: 'number',
        },
        {
          label: 'Chg',
          field: 'rentChg',
          width: '55px',
          align: 'right',
          type: 'number',
        },
        {
          label: 'Amount',
          field: 'amount',
          width: '65px',
          align: 'right',
          type: 'number',
        },
        {
          label: 'Bal',
          field: 'balance',
          width: '65px',
          align: 'right',
          type: 'number',
        },
        {
          label: 'B.R.',
          field: 'balanceRatio',
          width: '30px',
          align: 'center',
          type: 'number',
        },
        {
          label: 'Paid On',
          field: 'paidOn',
          width: '50px',
          align: 'center',
          type: 'date',
        },
        {
          label: 'Age',
          field: 'aging',
          width: '30px',
          align: 'center',
          type: 'number',
        },
        {
          label: 'LStat',
          field: 'leaseState',
          width: '40px',
          align: 'center',
          type: 'string',
        },
        {
          label: 'PStat',
          field: 'propertyState',
          width: '40px',
          align: 'center',
          type: 'string',
        },
        {
          label: 'Note',
          field: 'noteCount',
          width: '30px',
          align: 'center',
          type: 'number',
        },
        {
          label: '',
          field: 'petIcon',
          width: '24px',
          align: 'center',
          type: 'string',
          icon: 'fas fa-paw',
        },
        {
          label: '',
          field: 'pixieLastAccess',
          width: '30px',
          align: 'center',
          type: 'date',
          icon: 'fas fa-mobile-alt',
        },
      ],
      selectedProperties: [], // Stores the list of selected rows
      lastSelectedIndex: null,
      sortField: '',
      sortOrder: 'asc',
      expiringWithin: 90, // Default value for expiringWithin
      showNotesPanel: false,
      noteType: 'property',
      noteId: null,
      leaseCalendar: [], // Data for the leases by month table
      months: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ],
      leaseSearchQuery: '', // Search query for leases by month tab
      year: new Date().getFullYear(),
      startingWithin: 14,
      selectedChart: 0,
      chartOptions: [
        {
          buttonLabel: 'Lease',
          url: '/api/property-lease-calendar/',
          title: 'Lease Calendar',
        },
        {
          buttonLabel: 'Income',
          url: '/api/property-lease-income/',
          title: 'Income Chart',
        },
        {
          buttonLabel: 'Balance',
          url: '/api/property-lease-balance/',
          title: 'Balance Chart',
        },
        {
          buttonLabel: 'gas',
          url: '/api/property-gas-summary',
          title: 'Gas',
        },
        {
          buttonLabel: 'Electric',
          url: '/api/property-lease-calendar/',
          title: 'Electricity (simulated)',
        },
        {
          buttonLabel: 'water',
          url: '/api/property-lease-calendar/',
          title: 'Water (simulated)',
        },
        {
          buttonLabel: 'Prop Tax',
          url: '/api/property-lease-calendar/',
          title: 'Prop Tax (simulated)',
        },
      ],
      daysBefore: 5,
      daysAfter: 14,
      paymentPercent: 0,
      paymentCount: 0,
      paymentsTotal: 0,
      showChart: false,
      chartData: [],
      showQuickEmails: false,
      quickEmailTo: 'dan@bigpropertymanagement.com',
      quickEmailSubject: '',
      quickEmailBody: '',
      selectedFiles: [],
      markedBody: '',
      includeBcc: false,
      showLedgerListPopup: false,
      ledgerList: [],
      showPDF: false,
      pdfUrl: '',
      useSelectedAttachments: false,
      isDragMode: false,
      popupPosition: { x: 300, y: 300 }, // Initial popup position
      currentPopup: null, // Holds the currently targeted popup reference
      currentIconOffset: { x: 0, y: 0 }, // Holds the icon offset for dynamic calculations
      clearMessageInterval: null,
      refreshDataInterval: null,
      yearToShow: currentYear,
      calcExpression: '',
      // Resign tracking data
      resignData: [],
      resignFilter: '',
      resignEndDateFilterIndex: -1, // Tracks which unique end date we're filtering by (-1 = no filter)
      resignUniqueEndDates: [], // Sorted unique end dates from resign data
      endDateFilterLabel: '', // Label shown under the button
      editingResign: null,
      showResignDialog: false,
      showLeaseEndChart: false,
      selectedResignId: null,
      resignSortField: '',
      resignSortOrder: 'asc',
      resignTableHovered: false,
      showResignEditHint: false,
      resignEditHintTimeout: null,
      resignIframeUrl: null,
      resignIframeTitle: '',
      resignIframeLoading: true,
    };
  },

  mounted() {
    this.connectSocket();
    let initialTab = this.$route.query.tab;
    if (initialTab) {
      // Map URL aliases to internal tab names
      if (initialTab === 'early') {
        initialTab = 'resigns';
      }
      this.activeTab = initialTab;
      // Fetch resign data if loading via URL
      if (initialTab === 'resigns') {
        this.fetchResigns();
      }
    } else {
      const storedTab = localStorage.getItem('activeTab');
      if (storedTab) {
        this.activeTab = storedTab; // Restore the last selected tab
        // Fetch resign data if restoring to resigns tab
        if (storedTab === 'resigns') {
          this.fetchResigns();
        }
      }
    }
    localStorage.getItem('selectedChart')
      ? (this.selectedChart = parseInt(localStorage.getItem('selectedChart')))
      : (this.selectedChart = 0);
    this.fetchLeaseCalendar(this.year); // Fetch current year data initially

    window.addEventListener('keydown', this.handleKeyDown);
    this.clearMessageInterval = setTimeout(() => {
      this.updateFooterMessage('');
    }, 10000);

    if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
    if (this.$refs.leaseSearchQuery) this.$refs.leaseSearchQuery.focus();

    // Set up interval to call getProperties every 5 minutes
    this.propertiesInterval = setInterval(() => {
      this.getProperties();
    }, 300000); // 300,000 ms = 5 minutes

    // Set up interval to refresh resigns every 2 minutes (without changing sort)
    this.resignsRefreshInterval = setInterval(() => {
      if (this.activeTab === 'resigns') {
        this.fetchResigns();
      }
    }, 120000); // 120,000 ms = 2 minutes

    // Listen for PaymentViewer address double-click to open iframe overlay
    eventBus.on('openLeaseSearchIframe', this.handleOpenLeaseSearchIframe);
    eventBus.on('openTenantSearchIframe', this.handleOpenTenantSearchIframe);
    eventBus.on('openNewPersonIframe', this.handleOpenNewPersonIframe);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown);

    // Clear the interval when the component is about to be unmounted
    if (this.propertiesInterval) {
      clearInterval(this.propertiesInterval);
    }
    if (this.resignsRefreshInterval) {
      clearInterval(this.resignsRefreshInterval);
    }
    eventBus.off('openLeaseSearchIframe', this.handleOpenLeaseSearchIframe);
    eventBus.off('openTenantSearchIframe', this.handleOpenTenantSearchIframe);
    eventBus.off('openNewPersonIframe', this.handleOpenNewPersonIframe);
  },

  computed: {
    // Computed property to determine if property[month] should be shown
    // if current year (yearToShow = year) then suppress values in
    // months greater than the current month, as they aren't meaningful
    shouldDisplayProperty() {
      return (property, month) => {
        return (
          property[month] !== '0.00' &&
          property[month] !== null &&
          property[month] !== '' &&
          !(
            this.yearToShow == this.year &&
            this.months.indexOf(month) > new Date().getMonth()
          )
        );
      };
    },

    // Computed property to determine the fallback display value
    elseDisplayValue() {
      return (month) => {
        return this.yearToShow == this.year &&
          this.months.indexOf(month) > new Date().getMonth()
          ? ' '
          : '-';
      };
    },

    years() {
      const currentYear = new Date().getFullYear();
      const startYear = 2024;
      const yearsArray = [];
      for (let year = startYear; year <= currentYear; year++) {
        yearsArray.push(year);
      }
      return yearsArray;
    },

    // Computed property to filter resign data
    filteredResigns() {
      let data = this.resignData;

      // Apply end-date cycling filter
      if (
        this.resignEndDateFilterIndex >= 0 &&
        this.resignEndDateFilterIndex < this.resignUniqueEndDates.length
      ) {
        const target = this.resignUniqueEndDates[this.resignEndDateFilterIndex];
        data = data.filter((row) => {
          if (!row.endsOnRaw) return false;
          const d = new Date(row.endsOnRaw);
          d.setHours(0, 0, 0, 0);
          return d.getTime() === target.getTime();
        });
      }

      // Apply text filter
      if (this.resignFilter) {
        const filter = this.resignFilter.toLowerCase();
        data = data.filter(
          (row) =>
            (row.address && row.address.toLowerCase().includes(filter)) ||
            (row.tenants && row.tenants.toLowerCase().includes(filter)) ||
            (row.status && row.status.toLowerCase().includes(filter)) ||
            (row.note && row.note.toLowerCase().includes(filter)) ||
            (row.leaseState && row.leaseState.toLowerCase().includes(filter)) ||
            (row.propertyState &&
              row.propertyState.toLowerCase().includes(filter)),
        );
      }

      // Apply sorting
      if (this.resignSortField) {
        const field = this.resignSortField;
        const multiplier = this.resignSortOrder === 'asc' ? 1 : -1;
        data = data.slice().sort((a, b) => {
          let valA = a[field];
          let valB = b[field];

          // Handle numeric fields
          if (
            field === 'currentRent' ||
            field === 'offer' ||
            field === 'newRent' ||
            field === 'avail' ||
            field === 'inclTrash'
          ) {
            valA = parseFloat(valA) || 0;
            valB = parseFloat(valB) || 0;
          }

          // Handle date fields - put blanks at end
          if (field === 'endsOnRaw' || field === 'lastContactedRaw') {
            // Sort blanks to the end regardless of sort direction
            if (!valA && !valB) return 0;
            if (!valA) return 1; // a is blank, put at end
            if (!valB) return -1; // b is blank, put at end
            valA = new Date(valA).getTime();
            valB = new Date(valB).getTime();
          }

          // Handle string fields - put blanks at end
          if (field === 'status') {
            if (!valA && !valB) return 0;
            if (!valA) return 1;
            if (!valB) return -1;
          }

          if (valA > valB) return 1 * multiplier;
          if (valA < valB) return -1 * multiplier;
          return 0;
        });
      }

      return data;
    },

    sortedPropertyData() {
      const sorted = this.filteredProperties.slice().sort((a, b) => {
        const ageA = isNaN(Number(a.aging)) ? Infinity : Number(a.aging);
        const ageB = isNaN(Number(b.aging)) ? Infinity : Number(b.aging);
        return ageA - ageB;
      });
      return sorted;
    },

    compiledMarkdown() {
      // Set up marked with GFM enabled
      marked.setOptions({
        gfm: true,
        breaks: true, // Optional: Enable line breaks like in GitHub comments
      });

      // Return the compiled HTML from markdown content
      return marked(this.markdownContent);
    },

    monthlySummary() {
      const summary = {};

      this.months.forEach((month) => {
        if (this.chartOptions[this.selectedChart].buttonLabel === 'Lease') {
          // Count properties with lease IDs for the month and sum the rent
          let leaseCount = 0;
          let totalRent = 0;

          this.filteredLeaseCalendar.forEach((property) => {
            if (property[month] && typeof property[month] === 'string') {
              leaseCount++;

              // Use RegExp to extract the rent amount after '<br>', accounting for optional span tags
              const rentMatch = property[month].match(
                /<br>(?:<span[^>]*>)?\$?(\d+(?:\.\d+)?)/,
              ); // Updated regex to handle optional span tags
              if (rentMatch && rentMatch[1]) {
                totalRent += parseFloat(rentMatch[1]);
              }
            }
          });

          // Store both lease count and total rent in summary
          summary[month] = {
            leaseCount,
            totalRent: totalRent.toFixed(2), // Format to 2 decimal places
          };
        } else if (
          this.chartOptions[this.selectedChart].buttonLabel === 'Income' ||
          this.chartOptions[this.selectedChart].buttonLabel === 'Balance'
        ) {
          // Sum the income or balance values for the month
          summary[month] = this.filteredLeaseCalendar.reduce(
            (sum, property) => {
              return sum + (parseFloat(property[month]) || 0);
            },
            0,
          );
        }
      });

      return summary;
    },

    totalVacancyMonths() {
      // Only count vacancy months for the Lease view
      if (this.chartOptions[this.selectedChart].buttonLabel !== 'Lease') {
        return 0;
      }

      let vacancyCount = 0;

      // Iterate through each property
      this.filteredLeaseCalendar.forEach((property) => {
        // Iterate through each month
        this.months.forEach((month) => {
          // Check if the month cell is empty (no lease data)
          // In the Lease view, empty cells have no property[month] or it's empty/null
          if (
            !property[month] ||
            property[month] === '' ||
            property[month] === '0.00' ||
            property[month] === null
          ) {
            // Only count if we're not in future months for current year
            const monthIndex = this.months.indexOf(month);
            const currentMonth = new Date().getMonth();
            const isCurrentYear = this.yearToShow === this.year;

            // Don't count future months in current year as vacancies
            if (!isCurrentYear || monthIndex <= currentMonth) {
              vacancyCount++;
            }
          }
        });
      });

      return vacancyCount;
    },

    filteredLeaseCalendar() {
      // Return the full leaseCalendar if there is no search query
      if (!this.leaseSearchQuery)
        return this.flattenLeaseCalendar(this.leaseCalendar);

      // Split the leaseSearchQuery into tokens by comma and trim whitespace
      const tokens = this.leaseSearchQuery
        .split(',')
        .map((token) => token.trim().toLowerCase());

      // If no valid tokens are found, return the full leaseCalendar
      if (tokens.length === 0 || tokens[0] === '') {
        return this.flattenLeaseCalendar(this.leaseCalendar);
      }

      // Start with the full leaseCalendar data
      let filteredResults = this.leaseCalendar;

      // Apply filtering sequentially based on each token
      tokens.forEach((token) => {
        filteredResults = filteredResults.filter((property) =>
          property.address.toLowerCase().includes(token),
        );
      });

      // Return the flattened results
      return this.flattenLeaseCalendar(filteredResults);
    },

    calculateUnleasedValue(filteredLeaseCalendar) {
      // Iterate over each property
      return filteredLeaseCalendar.reduce((totalUnleasedValue, property) => {
        const months = Object.values(property.monthlyValues); // Assuming monthly values are stored in an object

        // Separate zero and non-zero month values
        const zeroValueMonths = months.filter((value) => value === 0).length;
        const nonZeroValues = months.filter((value) => value !== 0);

        // Calculate the average of non-zero months
        const nonZeroAverage =
          nonZeroValues.reduce((sum, val) => sum + val, 0) /
            nonZeroValues.length || 0;

        // Complete the calculation for the current property
        const unleasedValueForProperty = zeroValueMonths * nonZeroAverage;

        // Accumulate the result
        return totalUnleasedValue + unleasedValueForProperty;
      }, 0);
    },

    filteredPropertiesTotal() {
      const filteredResults = this.filteredProperties;
      const totalsRow = filteredResults.reduce(
        (totals, property) => {
          // Add each property value, converting strings to numbers and defaulting to 0 if necessary
          // Exclude expired/vacant properties from rent total
          totals.monthlyBaseRent +=
            property.expired !== 1 ? +property.monthlyBaseRent || 0 : 0;
          totals.rentChg += +property.rentChg || 0;
          totals.amount += +property.amount || 0;
          totals.balance += +property.balance || 0;
          return totals;
        },
        {
          propertyId: 0,
          address: 'Totals',
          name: 0,
          tenants: 0,
          monthlyBaseRent: 0,
          rentChg: 0,
          amount: 0,
          balance: 0,
        },
      );

      // Round the totals as the final step
      totalsRow.name = filteredResults.length + ' properties';
      totalsRow.monthlyBaseRent = Math.round(totalsRow.monthlyBaseRent);
      totalsRow.tenants =
        'annualized: $' +
        Math.round(totalsRow.monthlyBaseRent * 12.0).toLocaleString('en-US');
      totalsRow.rentChg = Math.round(totalsRow.rentChg * 12);
      totalsRow.amount = Math.round(totalsRow.amount).toLocaleString('en-US');
      totalsRow.balance = Math.round(totalsRow.balance).toLocaleString('en-US');

      return [totalsRow, ...filteredResults];
    },

    filteredProperties() {
      if (!this.propertyData) return [];

      let filteredResults = this.propertyData;

      if (this.searchQuery == ':lp') {
        const currentDate = new Date();
        const sixthOfMonth = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          6,
        );

        filteredResults = this.propertyData.filter((property) => {
          const paidOnDate = new Date(property.paidOn);
          return property.balance <= 0 && paidOnDate >= sixthOfMonth;
        });
        /**
         * Filter properties that are expired or expiring within 30 days
         * and don't have a future renewal date set.
         * This is triggered by using ":ex" search query.
         */
      } else if (this.searchQuery == ':le') {
        const selectedYear = this.leaseEndingsYear;
        filteredResults = this.propertyData.filter((property) => {
          if (!property.endsOn) return false;
          const endsOnDate = new Date(property.endsOn);
          return endsOnDate.getFullYear() === selectedYear;
        });
        this.sortBy('endsOn', 'asc');
      } else if (this.searchQuery == ':lenr') {
        const selectedYear = this.leaseEndingsYear;
        filteredResults = this.propertyData.filter((property) => {
          if (!property.endsOn) return false;
          const endsOnDate = new Date(property.endsOn);
          // Lease ends in selected year AND not renewed (sameTenants != 1)
          return (
            endsOnDate.getFullYear() === selectedYear &&
            property.sameTenants !== 1
          );
        });
        this.sortBy('endsOn', 'asc');
      } else if (this.searchQuery == ':ex') {
        filteredResults = this.propertyData.filter(
          (property) =>
            (property.expired === 1 ||
              this.isExpiringSoon(property.endsOn, 30)) &&
            property.futureStartsOn === null,
        );
        this.sortBy('endsOn', 'asc');
      } else if (this.searchQuery == ':ex30') {
        filteredResults = this.propertyData.filter(
          (property) =>
            (property.expired === 1 ||
              this.isExpiringSoon(property.endsOn, 30)) &&
            property.futureStartsOn === null,
        );
        this.sortBy('endsOn', 'asc');
      } else if (this.searchQuery == ':ex60') {
        filteredResults = this.propertyData.filter(
          (property) =>
            this.isExpiringSoon(property.endsOn, 30, 60) &&
            property.futureStartsOn === null,
        );
        this.sortBy('endsOn', 'asc');
      } else if (this.searchQuery == ':exAll') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        filteredResults = this.propertyData.filter((property) => {
          if (!property.startsOn || !property.endsOn) return false;
          const start = new Date(property.startsOn);
          const end = new Date(property.endsOn);
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          const isCurrent = start <= today && today <= end;
          return isCurrent && property.futureStartsOn === null;
        });
        this.sortBy('endsOn', 'asc');
      } else if (this.searchQuery == ':tl') {
        filteredResults = this.propertyData.filter(
          (property) => property.leaseState === 'TL',
        );
      } else if (this.searchQuery == ':e') {
        filteredResults = this.propertyData.filter(
          (property) => property.earlyResign === 1,
        );
      } else if (this.searchQuery == ':!b') {
        filteredResults = this.propertyData.filter(
          (property) => property.balance < 0,
        );
      } else if (this.searchQuery == ':!br') {
        filteredResults = this.propertyData.filter(
          (property) => property.balanceRatio <= -0.9,
        );
      } else if (this.searchQuery == ':invP') {
        filteredResults = this.propertyData.filter(
          (property) => property.invalidPayment === 1,
        );
      } else if (this.searchQuery == ':z') {
        filteredResults = this.propertyData.filter(
          (property) => property.balance == 0,
        );
      } else if (this.searchQuery == ':b') {
        filteredResults = this.propertyData.filter(
          (property) => property.balance > 0,
        );
      } else if (this.searchQuery == ':p') {
        return this.propertyData;
      } else if (this.searchQuery == ':s') {
        filteredResults = this.propertyData.filter(
          (property) => property.leaseState != '',
        );
      } else if (this.searchQuery == ':sel') {
        filteredResults = this.propertyData.filter((property) => {
          return this.selectedProperties.some((selectedProperty) => {
            const selectedPropertyId = selectedProperty?.propertyId;
            const propertyId = property?.propertyId;
            if (selectedPropertyId !== undefined && propertyId !== undefined) {
              return selectedPropertyId === propertyId;
            }

            return false; // Return false if either ID is undefined
          });
        });
      } else if (this.searchQuery == ':v') {
        filteredResults = this.propertyData.filter(
          (property) => property.expired === 1,
        );
      } else if (this.searchQuery == ':!v') {
        filteredResults = this.propertyData.filter(
          (property) => property.expired !== 1,
        );
      } else if (this.searchQuery == ':c') {
        filteredResults = this.propertyData.filter(
          (property) => property.checked === 1,
        );
      } else {
        const tokens = this.searchQuery
          .split(',')
          .map((token) => token.trim().toLowerCase());

        const normalizedTokens = tokens.map((token) =>
          token.replace(/^['"]|['"]$/g, ''),
        );
        const bracketFilters = normalizedTokens.filter((token) =>
          token.match(/^\[.*\]$/),
        );
        const regularTokens = normalizedTokens.filter(
          (token) => !token.match(/^\[.*\]$/),
        );

        if (bracketFilters.length > 0) {
          bracketFilters.forEach((bracket) => {
            const filterType = bracket.slice(1, -1).toLowerCase();

            if (filterType === 'single') {
              filteredResults = filteredResults.filter((property) => {
                const parentChildCount = Number(property.parentChildCount);
                if (Number.isFinite(parentChildCount) && parentChildCount > 0) {
                  return parentChildCount === 1;
                }
                const parentIdNum = Number(property.parentId);
                return !Number.isFinite(parentIdNum) || parentIdNum <= 0;
              });
            } else if (filterType === 'multi') {
              filteredResults = filteredResults.filter((property) => {
                const parentChildCount = Number(property.parentChildCount);
                if (Number.isFinite(parentChildCount) && parentChildCount > 0) {
                  return parentChildCount > 1;
                }
                const parentIdNum = Number(property.parentId);
                return Number.isFinite(parentIdNum) && parentIdNum > 0;
              });
            }
          });
        }

        if (regularTokens.length === 0 || regularTokens[0] === '') {
          return bracketFilters.length > 0
            ? filteredResults
            : this.propertyData;
        }

        regularTokens.forEach((token) => {
          filteredResults = filteredResults.filter((property) => {
            const propertyValues = Object.values(property).map((value) =>
              value ? value.toString().toLowerCase() : '',
            );

            return propertyValues.some((value) => value.includes(token));
          });
        });
      }

      return filteredResults;
    },

    formattedPaymentsTotal() {
      return `$${parseFloat(this.paymentsTotal)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    },
  },

  methods: {
    // Function to connect to WebSocket server
    connectSocket() {
      try {
        const socketUrl = process.env.VUE_APP_SOCKET_URL;

        if (!socketUrl) {
          console.error('Socket URL is undefined.');
          return;
        }

        const token = this.getAuthToken();

        this.socket = io(socketUrl, {
          query: {
            token: token,
            source: 'docuScan',
          },
        });

        this.socket.on('connect', () => {
          console.log(
            'ManageProperties: Connected to WebSocket server with ID:',
            this.socket.id,
          );
          this.updateFooterMessage('Connected to real-time updates');
        });

        // Handle reconnection events - this is key for server restart detection
        this.socket.on('reconnect', () => {
          // console.log( 'ManageProperties: Reconnected to server after attempt', attemptNumber,);
          this.updateFooterMessage('Reconnected to server. Refreshing data...');

          // Refresh data when socket reconnects - this catches server restarts
          this.getProperties();
          this.fetchLeaseCalendar(this.year);
        });

        this.socket.on('leasePaymentUpdated', () => {
          // Process the single payment object
          this.fetchProperties();
        });

        this.socket.on('resign-updated', () => {
          // Refresh resign data when updated from another session
          if (this.activeTab === 'resigns') {
            this.fetchResigns();
          }
        });

        // Also keep the explicit serverRestart handler
        this.socket.on('serverRestart', (timestamp) => {
          // Format the timestamp for display
          const restartTime = new Date(timestamp).toLocaleString();
          this.updateFooterMessage(
            `Server restarted at ${restartTime}. Refreshing data...`,
          );

          // Refresh data as if component was just mounted
          this.getProperties();
          this.fetchLeaseCalendar(this.year);
        });

        this.socket.on('disconnect', () => {
          this.updateFooterMessage(
            'Disconnected from real-time updates. Waiting to reconnect...',
          );
        });

        this.socket.on('connect_error', (error) => {
          console.error('ManageProperties: Socket connection error:', error);
          this.updateFooterMessage('Connection error: ' + error.message);
        });
      } catch (error) {
        console.error('Error connecting to WebSocket server:', error);
      }
    },

    getAuthToken() {
      return localStorage.getItem('token');
    },

    doCalc() {
      try {
        // Use eval to evaluate the expression safely; ensure only valid math expressions are executed.
        const result = eval(this.calcExpression);
        if (typeof result === 'number' && !isNaN(result)) {
          // Format the result to include commas and two decimal places
          this.calcResult = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(result);
        } else {
          this.calcResult = '';
        }
      } catch (error) {
        // In case of an error, set calcResult to an empty string
        this.calcResult = '';
      }
    },

    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(text)
          .then(() => {
            this.copyMessage = 'Copied!';
            setTimeout(() => {
              this.copyMessage = ''; // Clear the message after 2 seconds
            }, 2000);
          })
          .catch((err) => {
            console.error('Failed to copy text: ', err);
          });
      } else {
        this.copyMessage = 'not copied! (only works in production)';
        setTimeout(() => {
          this.copyMessage = ''; // Clear the message after 2 seconds
        }, 2000);
      }
    },

    cycleExpirationFilter() {
      // Cycle through the four states: '' -> ':ex30' -> ':ex60' -> ':exAll' -> ''
      if (this.expirationFilterState === '') {
        this.expirationFilterState = ':ex30';
        this.searchQuery = ':ex30';
      } else if (this.expirationFilterState === ':ex30') {
        this.expirationFilterState = ':ex60';
        this.searchQuery = ':ex60';
      } else if (this.expirationFilterState === ':ex60') {
        this.expirationFilterState = ':exAll';
        this.searchQuery = ':exAll';
      } else {
        this.expirationFilterState = '';
        this.searchQuery = '';
      }
    },

    cycleEndDateFilter() {
      // Build sorted list of unique end dates from resign data on first click or after reset
      if (this.resignEndDateFilterIndex === -1) {
        const dateSet = new Set();
        this.resignData.forEach((r) => {
          if (r.endsOnRaw) {
            const d = new Date(r.endsOnRaw);
            d.setHours(0, 0, 0, 0);
            dateSet.add(d.getTime());
          }
        });
        this.resignUniqueEndDates = Array.from(dateSet)
          .sort((a, b) => a - b)
          .map((ts) => new Date(ts));
        if (this.resignUniqueEndDates.length === 0) return;
      }

      // Advance to the next unique end date
      this.resignEndDateFilterIndex++;

      // If past the last date, reset
      if (this.resignEndDateFilterIndex >= this.resignUniqueEndDates.length) {
        this.resignEndDateFilterIndex = -1;
        this.endDateFilterLabel = '';
        this.resignUniqueEndDates = [];
        return;
      }

      const targetDate =
        this.resignUniqueEndDates[this.resignEndDateFilterIndex];
      this.endDateFilterLabel = targetDate.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: '2-digit',
      });
      // Sort by end date ascending
      this.resignSortField = 'endsOnRaw';
      this.resignSortOrder = 'asc';
    },

    openLeaseSearch(property) {
      const resolved = this.$router.resolve({
        path: '/lease-search',
        query: { search: ':' + property.leaseId, hideNav: '1' },
      });
      this.resignIframeTitle = property.address;
      this.resignIframeLoading = true;
      this.resignIframeUrl = resolved.href;
    },

    getSquareColor(property) {
      const monthlyBaseRent = parseFloat(property.monthlyBaseRent);
      const amount = parseFloat(property.amount);

      if (property.balance >= 0) {
        return '#00FF00'; // bright green
      } else if (property.balance >= -100) {
        return '#00E500'; // bright green
      } else if (property.aging < 3 && amount >= 0.9 * monthlyBaseRent) {
        return '#00FF00'; // bright green
      } else if (property.aging < 8 && amount >= 0.9 * monthlyBaseRent) {
        return '#00E500'; // darker shade of green by 15%
      } else if (property.aging < 8 && amount >= 0.5 * monthlyBaseRent) {
        return '#00B300'; // darker shade of green by 30%
      } else if (property.aging < 8 && amount < 0.5 * monthlyBaseRent) {
        return '#00A300'; // darker shade of green by 30%
      } else if (property.aging >= 8 && property.aging < 15) {
        return '#FFFF00'; // yellow
      } else if (property.aging >= 15 && property.aging < 30) {
        return '#FFD700'; // darker yellow
      } else if (property.aging >= 30 && property.aging < 45) {
        return '#FFA500'; // orange
      } else if (property.aging >= 45) {
        return '#f75a5a'; // light red
      }
      return '#FFFFFF'; // default color if none match
    },

    openModal() {
      this.showQuickEmails = true;
    },

    openLeaseStateDialog(leaseId) {
      this.selectedLeaseId = leaseId;
      this.showStatesDialog = true;
    },

    openPropertyStateDialog(propertyId) {
      this.selectedPropertyId = propertyId;
      this.selectedLeaseId = null; // Clear lease ID when opening property state
      this.showStatesDialog = true;
    },

    closeLeaseStateDialog() {
      this.showStatesDialog = false;
      this.getProperties();
    },

    handleKeyDown(event) {
      // Ignore keypresses if focus is on an input, textarea, or select
      const tag = event.target.tagName.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') {
        return;
      }

      // Handle 'e' key for editing resign row when hovering over resign table
      if (
        event.key === 'e' &&
        this.activeTab === 'resigns' &&
        this.resignTableHovered &&
        this.selectedResignId &&
        !this.showResignDialog
      ) {
        event.preventDefault();
        this.editSelectedResign();
        return;
      }

      // Handle 'r' key to sort by most recent (modifiedOnRaw desc) when hovering over resign table
      if (
        event.key === 'r' &&
        this.activeTab === 'resigns' &&
        this.resignTableHovered &&
        !this.showResignDialog
      ) {
        event.preventDefault();
        this.resignSortField = 'modifiedOnRaw';
        this.resignSortOrder = 'desc';
        return;
      }

      // If the Escape key is pressed
      if (event.key === 'Escape') {
        // if there is no query, refresh
        if (this.searchQuery === '') {
          switch (this.activeTab) {
            case 'overview':
              this.fetchProperties();
              this.updateFooterMessage('refreshing property overview data...');
              this.$refs.searchQuery ? this.$refs.searchQuery.focus() : null;
              break;
            case 'endings':
              this.fetchProperties();
              this.updateFooterMessage('refreshing lease endings data...');
              this.$refs.searchQuery ? this.$refs.searchQuery.focus() : null;
              break;
            case 'leasesByMonth':
              this.fetchLeaseCalendar(this.year);
              this.updateFooterMessage('refreshing calendar data...');
              break;
            case 'resigns':
              // Reset sort to default (by address)
              this.resignSortField = '';
              this.resignSortOrder = 'asc';
              this.resignFilter = '';
              this.selectedResignId = null;
              this.resignEndDateFilterIndex = -1;
              this.endDateFilterLabel = '';
              this.resignUniqueEndDates = [];
              this.fetchResigns();
              this.updateFooterMessage('refreshing early resigns data...');
              this.$refs.resignFilterInput
                ? this.$refs.resignFilterInput.focus()
                : null;
              break;
          }
        }
        if (this.selectedProperties.length === 0) {
          // If there are no selected payments, clear the search query
          this.searchQuery = '';
          this.$refs.searchQuery ? this.$refs.searchQuery.focus() : null;
          this.leaseSearchQuery = '';
        } else {
          // Otherwise, clear the selected payments
          this.selectedProperties = [];
          // Update local storage or any other related behavior
          localStorage.removeItem('selectedProperties');
        }
      }
    },

    getProperties() {
      this.updateFooterMessage('getting properties data...');
      this.fetchProperties().then(() => {
        // Check for saved properties in local storage
        const savedProperties = localStorage.getItem('selectedProperties');

        if (savedProperties) {
          try {
            this.selectedProperties = JSON.parse(savedProperties) || [];

            // Ensure the selected properties are valid and match an item in propertyData
            this.selectedProperties = this.selectedProperties.filter(
              (savedProp) => {
                // Ensure savedProp is neither null nor undefined before accessing its propertyId
                if (savedProp && savedProp.propertyId) {
                  return this.propertyData.find(
                    (property) => property.propertyId === savedProp.propertyId,
                  );
                }
                return false; // Exclude null or undefined savedProp
              },
            );

            if (this.selectedProperties.length === 0) {
              localStorage.removeItem('selectedProperties');
            }
          } catch (error) {
            console.error(
              'Error parsing selectedProperties from local storage:',
              error,
            );
            this.selectedProperties = [];
            localStorage.removeItem('selectedProperties');
          }
        }
      });
    },

    debounce(func, delay) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout); // Clear the previous timeout
        timeout = setTimeout(() => {
          func.apply(this, args); // Apply the function after the delay
        }, delay);
      }.bind(this);
    },

    formatNumber(value, decimals) {
      if (typeof value !== 'number') value = parseFloat(value);
      if (isNaN(value)) return '';
      if (value === 0.0) return '0.00';
      const sign = value < 0 ? '- ' : '';
      return `${sign}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}`;
    },

    highlightResignSearch(text) {
      if (!text) return '';
      // Map status values to display text
      const statusDisplayMap = {
        completed: 'Completed Docusign',
      };
      const displayText = statusDisplayMap[text] || text;
      if (!this.resignFilter) return displayText;
      const search = this.resignFilter.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${search})`, 'gi');
      return displayText.replace(
        regex,
        '<span class="resign-search-highlight">$1</span>',
      );
    },

    switchTab(tabName) {
      this.activeTab = tabName;
      this.showChart = false;
      localStorage.setItem('activeTab', tabName); // Store selected tab in localStorage
      this.updatePageTitle(tabName);

      // Update URL with tab parameter
      const urlTabName = tabName === 'resigns' ? 'early' : tabName;
      if (this.$route.query.tab !== urlTabName) {
        this.$router.replace({
          query: { ...this.$route.query, tab: urlTabName },
        });
      }

      // Refresh data when switching to overview or endings tabs
      if (tabName === 'overview' || tabName === 'endings') {
        this.fetchProperties();
      }

      // Fetch resign data when switching to resigns tab
      if (tabName === 'resigns') {
        this.fetchResigns();
      }

      // Set search query to ":le" when switching to endings tab
      if (tabName === 'endings') {
        this.searchQuery = ':le';
      } else if (tabName === 'overview') {
        // Clear search query when switching to overview tab
        this.searchQuery = '';
      }

      this.$nextTick(() => {
        if (this.$refs.leaseSearchQuery) this.$refs.leaseSearchQuery.focus();
        if (this.$refs.searchQuery) this.$refs.searchQuery.focus();
      });
    },

    setLeaseEndingsYear(year) {
      this.leaseEndingsYear = year;
      // Fetch new data for the selected year
      this.fetchProperties();
    },

    updatePageTitle(tabName = this.$route.query.tab) {
      document.title =
        tabName === 'payments' ? 'payments' : 'Manage Properties';
    },

    // ============================================
    // RESIGN TRACKING METHODS
    // ============================================

    async fetchResigns() {
      try {
        const response = await axios.get('/api/resign-summary');
        this.resignData = response.data;
      } catch (error) {
        console.error('Error fetching resign data:', error);
        this.updateFooterMessage('Error loading resign data');
      }
    },

    editResign(row) {
      this.editingResign = { ...row };
      // Default trashFee to 25 if not set
      if (
        this.editingResign.trashFee === null ||
        this.editingResign.trashFee === undefined
      ) {
        this.editingResign.trashFee = 25;
      }
      // Default newRent to currentRent + trashFee if not set
      if (!this.editingResign.newRent && this.editingResign.currentRent) {
        const currentRent = parseFloat(this.editingResign.currentRent) || 0;
        const trashFee = parseFloat(this.editingResign.trashFee) || 0;
        this.editingResign.newRent = currentRent + trashFee;
      }
      this.showResignDialog = true;
    },

    editSelectedResign() {
      // Edit the currently selected resign row
      if (!this.selectedResignId) return;
      const row = this.resignData.find(
        (r) => r.propertyId === this.selectedResignId,
      );
      if (row) {
        this.editResign(row);
      }
    },

    async handleResignDoubleClick(event, row) {
      const clickedElement = event.target.closest('td');
      console.log(
        '[resign dblclick] event.target:',
        event.target.tagName,
        event.target.className,
      );
      console.log(
        '[resign dblclick] closest td:',
        clickedElement?.tagName,
        clickedElement?.className,
      );
      console.log('[resign dblclick] row.address:', row.address);

      // Check if clicked on address cell - open Lease Search in iframe
      if (
        clickedElement &&
        clickedElement.classList.contains('resign-address-cell')
      ) {
        const resolved = this.$router.resolve({
          path: '/lease-search',
          query: { search: row.address.replace('#', ''), hideNav: '1' },
        });
        console.log('[resign dblclick] opening iframe:', resolved.href);
        this.resignIframeTitle = row.address;
        this.resignIframeLoading = true;
        this.resignIframeUrl = resolved.href;
        event.preventDefault();
        return;
      }

      // Check if clicked on tenants cell - open Quick Email and set lastContacted
      if (
        clickedElement &&
        clickedElement.classList.contains('resign-tenants-cell')
      ) {
        // CTRL+double-click: open search mode only (don't update lastContacted)
        if (event.ctrlKey && row.emails) {
          eventBus.emit('showQuickEmail');
          this.$nextTick(() => {
            eventBus.emit('populateEmailSearch', {
              emails: row.emails,
              searchOnly: true,
            });
          });
          event.preventDefault();
          return;
        }

        // Regular double-click: update lastContacted to today
        const today = new Date().toISOString().split('T')[0];
        try {
          await axios.post('/api/resign', {
            propertyId: row.propertyId,
            avail: row.avail,
            offer: row.offer,
            newRent: row.newRent,
            inclTrash: row.inclTrash,
            trashFee: row.trashFee,
            lastContacted: today,
            status: row.status,
            note: row.note,
          });
          // Update local data
          row.lastContacted = new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
          });
          row.lastContactedRaw = today;
          this.updateFooterMessage(`${row.address} - contacted today`);
        } catch (error) {
          console.error('Error updating lastContacted:', error);
        }

        // Open Quick Email with resign template
        if (row.emails) {
          const emailList = row.emails
            .split(',')
            .filter((email) => email)
            .map((email) => email.trim())
            .join(', ');
          const sendSource =
            typeof row.sendEmails === 'string' && row.sendEmails.trim()
              ? row.sendEmails
              : row.emails;
          const sendList = sendSource
            .split(',')
            .filter((email) => email)
            .map((email) => email.trim())
            .join(', ');

          // Calculate rent increase amount
          const currentRent = parseFloat(row.currentRent) || 0;
          const newRent = parseFloat(row.newRent) || currentRent;
          const amount = newRent - currentRent;

          eventBus.emit('showQuickEmail');
          this.$nextTick(() => {
            eventBus.emit('populateEmailSearch', {
              emails: emailList,
              to: sendList,
            });
            // If avail is false, open with blank body; otherwise use resignLease template
            if (row.avail === 0 || row.avail === false) {
              eventBus.emit('openQuickEmail', {
                to: sendList,
                body: '',
              });
            } else {
              eventBus.emit('openQuickEmail', {
                to: sendList,
                template: 'resignLease',
                params: {
                  address: row.address,
                  amount: amount > 0 ? `$${amount}` : '$0',
                  'new rent': newRent > 0 ? `$${newRent.toLocaleString()}` : '',
                  year: new Date().getFullYear().toString(),
                  inclTrash: row.inclTrash,
                  trashFee: row.trashFee || 25,
                  balance: row.balance || 0,
                },
              });
            }
          });
        } else {
          this.updateFooterMessage('No email addresses for this tenant');
        }
        event.preventDefault();
        return;
      }

      // Default: open edit dialog
      this.editResign(row);
    },

    async saveResign() {
      try {
        const payload = {
          propertyId: this.editingResign.propertyId,
          avail: this.editingResign.avail,
          offer: this.editingResign.offer,
          newRent: this.editingResign.newRent || null,
          pet: this.editingResign.pet || null,
          inclTrash: this.editingResign.inclTrash,
          trashFee: this.editingResign.trashFee || 25,
          lastContacted: this.editingResign.lastContactedRaw
            ? this.editingResign.lastContactedRaw.split('T')[0]
            : null,
          status: this.editingResign.status || '',
          note: this.editingResign.note || '',
        };

        await axios.post('/api/resign', payload);
        this.showResignDialog = false;
        this.editingResign = null;
        this.fetchResigns();
        this.updateFooterMessage('Resign record saved');
      } catch (error) {
        console.error('Error saving resign record:', error);
        this.updateFooterMessage('Error saving resign record');
      }
    },

    async toggleResignAvail(row) {
      try {
        const newAvail = row.avail === 1 ? 0 : 1;
        await axios.post('/api/resign', {
          propertyId: row.propertyId,
          avail: newAvail,
          offer: row.offer,
          newRent: row.newRent,
          pet: row.pet,
          inclTrash: row.inclTrash,
          trashFee: row.trashFee,
          lastContacted: row.lastContactedRaw
            ? row.lastContactedRaw.split('T')[0]
            : null,
          status: row.status,
          note: row.note,
        });
        row.avail = newAvail;
        this.updateFooterMessage(
          `${row.address} - availability ${newAvail === 1 ? 'enabled' : 'disabled'}`,
        );
      } catch (error) {
        console.error('Error toggling resign availability:', error);
      }
    },

    async toggleResignInclTrash(row) {
      try {
        const newInclTrash = row.inclTrash === 1 ? 0 : 1;
        const newTrashFee = newInclTrash === 1 ? 25 : 0;
        await axios.post('/api/resign', {
          propertyId: row.propertyId,
          avail: row.avail,
          offer: row.offer,
          newRent: row.newRent,
          pet: row.pet,
          inclTrash: newInclTrash,
          trashFee: newTrashFee,
          lastContacted: row.lastContactedRaw
            ? row.lastContactedRaw.split('T')[0]
            : null,
          status: row.status,
          note: row.note,
        });
        row.inclTrash = newInclTrash;
        row.trashFee = newTrashFee;
        this.updateFooterMessage(
          `${row.address} - trash ${newInclTrash === 1 ? 'included' : 'excluded'}`,
        );
      } catch (error) {
        console.error('Error toggling resign inclTrash:', error);
      }
    },

    sortResignsBy(field) {
      if (this.resignSortField === field) {
        this.resignSortOrder = this.resignSortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.resignSortField = field;
        // Default to descending for date columns, ascending for others
        this.resignSortOrder = field === 'modifiedOnRaw' ? 'desc' : 'asc';
      }
    },

    getNewRentBackground(newRent, currentRent, trashFee) {
      if (!newRent || !currentRent) return 'transparent';
      const newVal = parseFloat(newRent);
      const currVal = parseFloat(currentRent);
      const trash = parseFloat(trashFee) || 0;
      if (isNaN(newVal) || isNaN(currVal)) return 'transparent';

      const expectedRent = currVal + trash;
      // Check if newRent diverges from expected (currentRent + trashFee)
      if (Math.abs(newVal - expectedRent) > 0.01) {
        return '#fff3cd'; // Yellow - divergent from expected
      }
      if (newVal > currVal) {
        return '#d4edda'; // Light green - rent increased
      } else if (newVal < currVal) {
        return '#ffcccc'; // Light red - rent decreased
      }
      return 'transparent';
    },

    getNewRentColor(newRent, currentRent, trashFee) {
      if (!newRent || !currentRent) return 'inherit';
      const newVal = parseFloat(newRent);
      const currVal = parseFloat(currentRent);
      const trash = parseFloat(trashFee) || 0;
      if (isNaN(newVal) || isNaN(currVal)) return 'inherit';

      const expectedRent = currVal + trash;
      // Check if newRent diverges from expected (currentRent + trashFee)
      if (Math.abs(newVal - expectedRent) > 0.01) {
        return '#856404'; // Dark yellow text - divergent from expected
      }
      if (newVal > currVal) {
        return '#155724'; // Dark green text
      } else if (newVal < currVal) {
        return '#cc0000'; // Red text
      }
      return 'inherit';
    },

    printResigns() {
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Early Resigns Report</title>
          <style type="text/css">
            @page { size: landscape; }
            @media print {
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
            body { font-family: Arial, sans-serif; font-size: 8pt; margin: 10px; }
            h2 { font-size: 12pt; margin-bottom: 10px; }
            table { border-collapse: collapse; width: 100%; table-layout: fixed; }
            th, td { border: 1px solid #ccc; padding: 3px 5px; text-align: left; font-size: 8pt; overflow: hidden; }
            th { background-color: #888 !important; color: #fff !important; }
            .even-row { background-color: #e0e0e0 !important; }
            .odd-row { background-color: #ffffff !important; }
            .col-address { width: 130px; }
            .col-tenants { width: 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px; }
            .col-ends { width: 70px; }
            .col-rent { width: 60px; }
            .col-pet { width: 50px; }
            .col-offer { width: 60px; }
            .col-newrent { width: 60px; }
            .col-status { width: 70px; }
            .col-note { width: auto; }
            .footer { text-align: center; font-size: 8pt; color: #888; margin-top: 30px; }
          </style>
        </head>
        <body>
        <h2>Early Resigns Report - ${new Date().toLocaleDateString()}</h2>
        <table>
          <thead>
            <tr><th class="col-address">Address</th><th class="col-tenants">Tenants</th><th class="col-ends">Ends</th><th class="col-rent">Rent</th><th class="col-pet">Pet</th><th class="col-offer">Offer</th><th class="col-newrent">New Rent</th><th class="col-status">Status</th><th class="col-note">Note</th></tr>
          </thead>
          <tbody>
            ${this.filteredResigns.map((r, i) => `<tr class="${i % 2 === 0 ? 'odd-row' : 'even-row'}"><td class="col-address">${r.address}</td><td class="col-tenants" title="${r.tenants}">${r.tenants}</td><td class="col-ends">${r.endsOn}</td><td class="col-rent">${r.currentRent}</td><td class="col-pet">${r.pet != null && r.pet !== '' ? r.pet : ''}</td><td class="col-offer">${r.offer}</td><td class="col-newrent">${r.newRent || ''}</td><td class="col-status">${r.status}</td><td class="col-note">${r.note || ''}</td></tr>`).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Pixie Properties</p>
          <p>${new Date().toLocaleString()}</p>
        </div>
        </body>
        </html>
      `;
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    },

    exportResignsExcel() {
      const headers = [
        'Address',
        'Tenants',
        'Ends',
        'Rent',
        'Avail',
        'Pet',
        'Offer',
        'New Rent',
        'Contacted',
        'Status',
        'Note',
      ];
      const rows = this.filteredResigns.map((r) => [
        r.address,
        r.tenants,
        r.endsOn,
        r.currentRent,
        r.avail,
        r.pet != null && r.pet !== '' ? r.pet : '',
        r.offer,
        r.newRent || '',
        r.lastContacted || '',
        r.status,
        r.note || '',
      ]);

      // Create a worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Apply column widths
      const wscols = [
        { wch: 25 }, // Address
        { wch: 30 }, // Tenants
        { wch: 12 }, // Ends
        { wch: 10 }, // Rent
        { wch: 12 }, // Avail
        { wch: 10 }, // Pet
        { wch: 10 }, // Offer
        { wch: 10 }, // New Rent
        { wch: 12 }, // Contacted
        { wch: 12 }, // Status
        { wch: 40 }, // Note
      ];
      ws['!cols'] = wscols;

      // Create workbook and add the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Early Resigns');

      // Generate Excel file and save
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([wbout], { type: 'application/octet-stream' }),
        `early-resigns-${new Date().toISOString().split('T')[0]}.xlsx`,
      );
    },

    displayRatio(balanceRatio) {
      const parsedRatio = parseFloat(balanceRatio);
      if (isNaN(parsedRatio)) {
        return ''; // Return an empty string if balanceRatio is not a number
      }

      if (parsedRatio > 12) {
        return ' ∞';
      } else if (parsedRatio < -12) {
        return '-∞';
      } else {
        return parsedRatio.toFixed(1); // Format to one decimal place
      }
    },

    // Helper methods for rentChg column styling
    hasRentChgValue(rentChg) {
      if (rentChg === null || rentChg === undefined || rentChg === '') {
        return false;
      }
      const value = parseFloat(rentChg);
      return !isNaN(value);
    },

    getRentChgDisplayValue(rentChg) {
      if (!this.hasRentChgValue(rentChg)) {
        return '';
      }

      const value = parseFloat(rentChg);

      if (value > 0) {
        return '+ ' + this.formatNumber(Math.abs(value), 0);
      } else if (value < 0) {
        return '- ' + this.formatNumber(Math.abs(value), 0);
      }

      return this.formatNumber(value, 0);
    },

    getRentChgBackgroundColor(rentChg, variant = 'standard') {
      if (!this.hasRentChgValue(rentChg)) {
        return 'transparent';
      }
      const value = parseFloat(rentChg);

      if (value > 0) {
        return variant === 'future' ? '#6fbf7f' : '#d4edda';
      } else if (value < 0) {
        return variant === 'future' ? '#d98888' : '#ffcccc';
      }

      return 'transparent';
    },

    getRentChgTextColor(rentChg, variant = 'standard') {
      if (!this.hasRentChgValue(rentChg)) {
        return 'inherit';
      }
      const value = parseFloat(rentChg);

      if (value > 0) {
        return variant === 'future' ? '#0b4a1e' : '#155724';
      } else if (value < 0) {
        return variant === 'future' ? '#8b0000' : '#cc0000';
      }

      return 'inherit';
    },

    getRentChgLineStyle(rentChg, variant = 'standard') {
      return {
        backgroundColor: this.getRentChgBackgroundColor(rentChg, variant),
        color: this.getRentChgTextColor(rentChg, variant),
      };
    },

    getRentChgCellTitle(property) {
      const parts = [];
      if (this.hasRentChgValue(property.rentChg)) {
        parts.push(
          `Current vs previous lease: ${this.getRentChgDisplayValue(property.rentChg)}`,
        );
      }
      if (this.hasRentChgValue(property.rentChgFuture)) {
        parts.push(
          `Future vs current lease: ${this.getRentChgDisplayValue(property.rentChgFuture)}`,
        );
      }
      parts.push('Double click to edit rent change (current vs previous)');
      return parts.join('\n');
    },

    async fetchProperties() {
      // Determine which query type to use based on active tab
      const queryType = this.activeTab === 'endings' ? 'endings' : 'overview';

      // Build params object
      const params = { type: queryType };
      if (queryType === 'endings') {
        params.year = this.leaseEndingsYear;
      }

      // Return the Axios promise
      return axios
        .get('/api/property-lease-summary', {
          params: params,
        })
        .then((response) => {
          // Assuming response.data contains the array of properties
          this.propertyData = response.data.map((property) => ({
            ...property,
            // balance: 0, // Initially setting balance to 0; this will be updated later
          }));

          this.getPaymentStats();
          // Return the data to indicate success
          this.getBalance();
          // re-sort
          this.sortProperties();
          return response.data;
        })
        .catch((error) => {
          console.error('Error fetching properties:', error);
          // Handle error appropriately (e.g., show a message to the user)
          throw error; // Rethrow the error to propagate it to the caller
        });
    },

    temperamentColor(value) {
      const colors = ['#aaa', '#4caf50', '#ffc107', '#f44336'];
      return colors[value] || colors[0];
    },

    isPixieAccessToday(dt) {
      if (!dt) return false;
      const d = new Date(dt);
      const diffMs = Date.now() - d.getTime();
      return diffMs >= 0 && diffMs <= 120 * 60 * 1000;
    },

    pixieAccessTooltip(property) {
      if (!property.pixieLastAccess) return '';
      const d = new Date(property.pixieLastAccess);
      const date = d.toLocaleString();
      const version = property.pixieVersion || '(unknown)';
      const deviceMap = {
        ios: 'iOS',
        android: 'Android',
        windows: 'Windows',
        mac: 'macOS',
        linux: 'Linux',
      };
      const deviceKey = (property.pixieDevice || '').toLowerCase();
      const device =
        deviceMap[deviceKey] || property.pixieDevice || '(unknown)';
      const browserMap = {
        chrome: 'Chrome',
        'chrome-ios': 'Chrome (iOS)',
        safari: 'Safari',
        firefox: 'Firefox',
        'firefox-ios': 'Firefox (iOS)',
        edge: 'Edge',
        opera: 'Opera',
        samsung: 'Samsung Internet',
      };
      const browserKey = (property.pixieBrowser || '').toLowerCase();
      const browser =
        browserMap[browserKey] || property.pixieBrowser || '(unknown)';
      const standaloneRaw = property.pixieStandalone;
      let pwa;
      if (
        standaloneRaw === 1 ||
        standaloneRaw === '1' ||
        standaloneRaw === true
      ) {
        pwa = 'Yes';
      } else if (
        standaloneRaw === 0 ||
        standaloneRaw === '0' ||
        standaloneRaw === false
      ) {
        pwa = 'No (browser tab)';
      } else {
        pwa = '(unknown)';
      }
      const notifications = formatPixieNotifications(property.pixieNotifications);
      return `Last PixieChatBot access: ${date}\nVersion: ${version}\nDevice: ${device}\nBrowser: ${browser}\nInstalled as PWA: ${pwa}\nNotifications: ${notifications}`;
    },

    petTooltip(property) {
      const labels = ['N/A', 'Friendly', 'Caution', 'Unfriendly'];
      const parts = [];
      if (
        property.petCount > 0 &&
        !property.dogCount &&
        !property.catCount &&
        !property.dogTemperament &&
        !property.catTemperament
      ) {
        parts.push(
          `${property.petCount} pet(s) - edit lease to set dog/cat details`,
        );
      }
      if (property.dogCount > 0) {
        parts.push(`Dog: ${labels[property.dogTemperament] || 'N/A'}`);
      }
      if (property.catCount > 0) {
        parts.push(`Cat: ${labels[property.catTemperament] || 'N/A'}`);
      }
      if (property.petNames) {
        parts.push(property.petNames);
      }
      return parts.join('\n') || '';
    },

    isExpired(endsOn) {
      if (!endsOn) return false;
      const endDate = new Date(endsOn);
      const today = new Date();

      // Reset time portion for accurate date comparison
      endDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Return true if today is equal to or after the end date
      return today > endDate;
    },

    hasRenewal(futureStartsOn) {
      if (!futureStartsOn) return false;
      const startDate = new Date(futureStartsOn);
      const today = new Date();

      // Reset time portion for accurate date comparison
      startDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Return true if today is before the start date
      return today < startDate;
    },
    isExpiringSoon(endsOn, minDays, maxDays) {
      if (!endsOn) return false;
      const endDate = new Date(endsOn);
      const today = new Date();
      const daysDiff = (endDate - today) / (1000 * 60 * 60 * 24); // Difference in days

      // If only one parameter is provided, treat it as maxDays (backward compatibility)
      if (maxDays === undefined && minDays !== undefined) {
        maxDays = minDays;
        minDays = 0;
      }

      // Use provided parameters or fall back to this.expiringWithin
      const maxThreshold =
        maxDays !== undefined ? maxDays : this.expiringWithin;
      const minThreshold = minDays !== undefined ? minDays : 0;

      return daysDiff >= minThreshold && daysDiff <= maxThreshold;
    },

    isStartingSoon(startsOn) {
      if (!startsOn) return false;
      const startDate = new Date(startsOn);
      const today = new Date();
      const daysDiff = (startDate - today) / (1000 * 60 * 60 * 24); // Difference in days
      return Math.abs(daysDiff) <= this.startingWithin;
    },

    clearSearch() {
      this.searchQuery = '';
      this.expirationFilterState = '';
      this.resignEndDateFilterIndex = -1;
      this.endDateFilterLabel = '';
      this.resignUniqueEndDates = [];
    },

    getBalance() {
      // Iterate over each property in propertyData and calculate aging based on the paidOn date
      this.propertyData = this.propertyData.map((property) => {
        // Calculate aging: difference in days between paidOn and today
        const today = new Date();
        let aging;
        if (property.paidOn) {
          const paidOnDate = new Date(property.paidOn);

          // Calculate the difference in milliseconds and convert to days
          aging = Math.floor((today - paidOnDate) / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
          aging > 365 ? (aging = '-') : aging;
        } else {
          aging = '-';
        }

        return {
          ...property,
          aging: aging, // Calculated aging
        };
      });
    },

    sortChecked() {
      this.sortOrder = 'asc';
      this.sortField = 'checked';
      this.sortBy('checked');
    },

    sortBy(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
      this.sortProperties();
    },

    sortProperties() {
      const field = this.sortField;
      const sortMultiplier = this.sortOrder === 'asc' ? 1 : -1;
      const header = this.propertyTableHeaders.find((h) => h.field === field);
      const type = header ? header.type : 'string'; // Default to 'string' if type is not found

      if (field === 'address') {
        this.propertyData.sort((a, b) => {
          let compareResult = 0;

          // Sort by StreetName
          const streetNameA = a.StreetName ? a.StreetName.toLowerCase() : '';
          const streetNameB = b.StreetName ? b.StreetName.toLowerCase() : '';

          if (streetNameA > streetNameB) compareResult = 1;
          else if (streetNameA < streetNameB) compareResult = -1;

          if (compareResult !== 0) return compareResult * sortMultiplier;

          // Sort by StreetNumber
          const streetNumberA = parseInt(a.StreetNumber, 10) || 0;
          const streetNumberB = parseInt(b.StreetNumber, 10) || 0;

          if (streetNumberA > streetNumberB) compareResult = 1;
          else if (streetNumberA < streetNumberB) compareResult = -1;

          if (compareResult !== 0) return compareResult * sortMultiplier;

          // Sort by UnitNumber as a number after removing non-digits
          const unitNumberA = parseInt(a.Unit.replace(/[^0-9]/g, ''), 10) || 0;
          const unitNumberB = parseInt(b.Unit.replace(/[^0-9]/g, ''), 10) || 0;

          if (unitNumberA > unitNumberB) compareResult = 1;
          else if (unitNumberA < unitNumberB) compareResult = -1;

          return compareResult * sortMultiplier;
        });
      } else {
        this.propertyData.sort((a, b) => {
          let aValue = a[field];
          let bValue = b[field];

          switch (type) {
            case 'date':
              if (aValue == null || aValue === '')
                aValue = this.sortOrder === 'asc' ? Infinity : -Infinity;
              else aValue = new Date(aValue);

              if (bValue == null || bValue === '')
                bValue = this.sortOrder === 'asc' ? Infinity : -Infinity;
              else bValue = new Date(bValue);

              if (isNaN(aValue))
                aValue = this.sortOrder === 'asc' ? Infinity : -Infinity;
              if (isNaN(bValue))
                bValue = this.sortOrder === 'asc' ? Infinity : -Infinity;
              break;

            case 'number':
              if (aValue == null || isNaN(aValue))
                aValue = this.sortOrder === 'asc' ? Infinity : -Infinity;
              else aValue = parseFloat(aValue);

              if (bValue == null || isNaN(bValue))
                bValue = this.sortOrder === 'asc' ? Infinity : -Infinity;
              else bValue = parseFloat(bValue);
              break;

            case 'boolean':
              aValue = !!aValue;
              bValue = !!bValue;
              break;

            case 'string':
            default:
              if (aValue == null || aValue === '') return 1 * sortMultiplier;
              if (bValue == null || bValue === '') return -1 * sortMultiplier;

              if (typeof aValue === 'string') aValue = aValue.toLowerCase();
              if (typeof bValue === 'string') bValue = bValue.toLowerCase();
              break;
          }

          if (aValue > bValue) return 1 * sortMultiplier;
          if (aValue < bValue) return -1 * sortMultiplier;
          return 0;
        });
      }
    },

    // Check if the clicked element is within the tenants column (icon or span)
    handleDoubleClick(event, property) {
      const clickedElement = event.target;

      // Check if the clicked element is within the Amount column
      if (clickedElement.classList.contains('amount-column')) {
        this.activeTab = 'payments';
        localStorage.setItem('activeTab', 'payments');
        // Emit event to PaymentViewer to search for the address after tab switches
        this.$nextTick(() => {
          eventBus.emit('searchPaymentsByAddress', property.address);
        });
        event.preventDefault();
        return;
      }

      // Check if the clicked element is within the specs column
      if (clickedElement.classList.contains('specs-column')) {
        this.handleSpecsColumnClick(event, property);
        event.preventDefault();
        return;
      }

      // Check if the clicked element is within the rentChg column
      if (clickedElement.closest?.('.rentchg-column')) {
        this.handleRentChgColumnClick(event, property);
        event.preventDefault();
        return;
      }

      // Check if the clicked element is within the tenants column (icon or span)
      if (clickedElement.classList.contains('tenant-emails')) {
        // Check if the CTRL key is pressed - search only mode
        if (event.ctrlKey && property.emails) {
          eventBus.emit('showQuickEmail');
          this.$nextTick(() => {
            eventBus.emit('populateEmailSearch', {
              emails: property.emails,
              searchOnly: true,
            });
          });
          event.preventDefault();
          return;
        }
        // Check if the SHIFT key is pressed - quick copy panel
        if (event.shiftKey) {
          event.preventDefault();
          this.showCopyPanel = true;
          console.log('Event coordinates:', event.clientX, event.clientY);

          // Position the showCopyPanel div at the click location using ref
          this.$nextTick(() => {
            console.log('Event coordinates:', event.clientX, event.clientY);

            if (this.$refs.copyPanel) {
              console.log('copyPanel:', this.$refs.copyPanel);
              this.$refs.copyPanel.style.position = 'absolute';
              this.$refs.copyPanel.style.left = `${event.clientX}px`;

              // Adjust top position if too close to the bottom of the screen
              const panelHeight = this.$refs.copyPanel.offsetHeight;
              const viewportHeight = window.innerHeight;
              let topPosition = event.clientY;

              if (topPosition + panelHeight > viewportHeight) {
                topPosition = viewportHeight - panelHeight - 100; // Adjust to ensure full visibility
              }

              this.$refs.copyPanel.style.top = `${topPosition}px`;
            }
          });

          // Set tenantsToCopy and emailsToCopy from the property object
          this.propertyToCopy =
            property.address + ' Grand Rapids, MI ' + property.Zipcode;
          this.rentToCopy = property.monthlyBaseRent;
          this.tenantsToCopy = property.tenants
            .split(',')
            .map((tenant) => tenant.trim()); // Split and trim each tenant

          const copySource =
            typeof property.sendEmails === 'string' &&
            property.sendEmails.trim()
              ? property.sendEmails
              : property.emails;
          this.emailsToCopy = copySource
            .split(',')
            .map((email) => email.trim()); // Split and trim each email
          this.phonesToCopy = property.phones
            .split(',')
            .map((phone) => phone.trim()); // Split and trim each email
          event.preventDefault();
          return;
        } else {
          // Join all emails with a comma and space
          const emailList = property.emails
            .split(',')
            .filter((email) => email) // Remove any empty strings
            .map((email) => email.trim()) // Trim each email
            .join(', ');
          const sendSource =
            typeof property.sendEmails === 'string' &&
            property.sendEmails.trim()
              ? property.sendEmails
              : property.emails;
          const sendList = sendSource
            .split(',')
            .filter((email) => email)
            .map((email) => email.trim())
            .join(', ');

          // console.log('Combined email list:', emailList);
          // First ensure QuickEmail is open, then populate search field
          eventBus.emit('showQuickEmail');
          // Use nextTick to ensure QuickEmail is rendered before populating
          this.$nextTick(() => {
            eventBus.emit('populateEmailSearch', {
              emails: emailList,
              to: sendList,
            });
          });
          event.preventDefault();
          return;
        }
      }

      // Check if the clicked element is within the notes column (icon or span)
      if (clickedElement.classList.contains('notes-column')) {
        this.handleNotesColumnClick(property);
      }
      // Check if the clicked element is in the checkbox column
      else if (clickedElement.classList.contains('toggle-check')) {
        // console.log('toggling checkmark');
        this.toggleCheckmark(property);
      }
      // Check if the clicked element is in the lease state column
      else if (clickedElement.classList.contains('lease-state')) {
        // Open the LeaseState dialog with the property leaseId
        this.openLeaseStateDialog(property.leaseId);
      }
      // Check if the clicked element is in the property state column
      else if (clickedElement.classList.contains('property-state')) {
        // Open the PropertyState dialog with the property propertyId
        this.openPropertyStateDialog(property.propertyId);
      }
      // Default behavior for double click
      else {
        this.navigateToLeaseSearch(property);
      }
    },

    handleNotesColumnClick(property) {
      // Handle the click in the notes column
      this.showNotesPanel = true;
      this.noteId = property.propertyId;
      this.noteType = 'property';
    },

    async handleSpecsColumnClick(event, property) {
      // Handle the click in the specs column
      this.selectedProperty = property;
      this.selectedSpecs = property.specs || '';
      this.newSpecs = '';

      // Fetch available specs options
      try {
        const response = await axios.get('/api/distinct-specs');
        this.availableSpecs = response.data;
      } catch (error) {
        console.error('Error fetching specs options:', error);
        this.availableSpecs = [];
      }

      this.showSpecsDialog = true;

      // Position the dialog at mouse click + 100px to the right
      this.$nextTick(() => {
        const dialog = this.$refs.specsDialog;
        if (dialog) {
          dialog.style.position = 'absolute';
          dialog.style.left = `${event.clientX + 100}px`;
          dialog.style.top = `${event.clientY}px`;
          dialog.style.zIndex = '1000';
        }
      });
    },

    async handleRentChgColumnClick(event, property) {
      // Handle the click in the rentChg column
      this.selectedProperty = property;
      this.selectedRentChg = property.rentChg || '';
      this.newRentChg = '';

      // Fetch available rentChg options
      try {
        const response = await axios.get('/api/distinct-rentchg');
        this.availableRentChg = response.data;
      } catch (error) {
        console.error('Error fetching rentChg options:', error);
        this.availableRentChg = [];
      }

      this.showRentChgDialog = true;

      // Position the dialog at mouse click + 100px to the right
      this.$nextTick(() => {
        const dialog = this.$refs.rentChgDialog;
        if (dialog) {
          dialog.style.position = 'absolute';
          dialog.style.left = `${event.clientX + 100}px`;
          dialog.style.top = `${event.clientY}px`;
          dialog.style.zIndex = '1000';
        }
      });
    },

    async saveSpecs() {
      if (!this.selectedProperty) return;

      const specsToSave = this.newSpecs || this.selectedSpecs;

      try {
        await axios.put('/api/update-property-specs', {
          propertyId: this.selectedProperty.propertyId,
          specs: specsToSave,
        });

        // Update the local property data
        const property = this.propertyData.find(
          (p) => p.propertyId === this.selectedProperty.propertyId,
        );
        if (property) {
          property.specs = specsToSave;
        }

        this.closeSpecsDialog();
      } catch (error) {
        console.error('Error updating specs:', error);
      }
    },

    closeSpecsDialog() {
      this.showSpecsDialog = false;
      this.selectedProperty = null;
      this.selectedSpecs = '';
      this.newSpecs = '';
    },

    async saveRentChg() {
      if (!this.selectedProperty) return;

      let rentChgToSave = this.newRentChg || this.selectedRentChg;

      // Convert "0.00" or "0" to null
      if (
        rentChgToSave === '0' ||
        rentChgToSave === '0.00' ||
        parseFloat(rentChgToSave) === 0
      ) {
        rentChgToSave = null;
      }

      try {
        await axios.put('/api/update-property-rentchg', {
          propertyId: this.selectedProperty.propertyId,
          rentChg: rentChgToSave,
        });

        // Update the local property data
        const property = this.propertyData.find(
          (p) => p.propertyId === this.selectedProperty.propertyId,
        );
        if (property) {
          property.rentChg = rentChgToSave;
        }

        this.closeRentChgDialog();
      } catch (error) {
        console.error('Error updating rentChg:', error);
      }
    },

    closeRentChgDialog() {
      this.showRentChgDialog = false;
      this.selectedProperty = null;
      this.selectedRentChg = '';
      this.newRentChg = '';
    },

    async handleStateClick(type, id, event) {
      // Handle single click vs double click
      if (this.clickTimeout) {
        // This is a double click - clear timeout and handle double click
        clearTimeout(this.clickTimeout);
        this.clickTimeout = null;
        this.handleStateDoubleClick(type, id);
        return;
      }

      // This is a single click - set timeout to handle it
      this.clickTimeout = setTimeout(() => {
        this.clickTimeout = null;
        this.showStateHistory(type, id, event);
      }, 250); // 250ms delay to distinguish single vs double click
    },

    handleStateDoubleClick(type, id) {
      // Handle double click - open the state editor dialog
      if (type === 'lease') {
        this.openLeaseStateDialog(id);
      } else {
        this.openPropertyStateDialog(id);
      }
    },

    async showStateHistory(type, id, event) {
      try {
        this.stateHistoryType = type;
        this.stateHistoryId = id;

        // Find the property/row associated with this state click and select it
        const property = this.propertyData.find((p) => {
          if (type === 'lease') {
            return p.leaseId === id;
          } else {
            return p.propertyId === id;
          }
        });

        if (property) {
          // Find the index in the filtered properties for proper selection
          const index = this.filteredPropertiesTotal.findIndex(
            (p) => p.propertyId === property.propertyId,
          );
          if (index !== -1) {
            this.selectedProperties = [property];
            this.lastSelectedIndex = index;

            // Update local storage
            localStorage.setItem(
              'selectedProperties',
              JSON.stringify(this.selectedProperties),
            );
          }
        }

        // Fetch state history for both lease and property
        const [leaseResponse, propertyResponse] = await Promise.all([
          axios.get(`/api/state-history/lease/${id}`),
          axios.get(`/api/state-history/property/${id}`),
        ]);

        this.stateHistoryData = {
          lease: leaseResponse.data,
          property: propertyResponse.data,
        };

        this.showStateHistoryDialog = true;

        // Position the dialog at mouse click location
        this.$nextTick(() => {
          const dialog = this.$refs.stateHistoryDialog;
          if (dialog) {
            dialog.style.position = 'absolute';

            // Get dialog dimensions
            const dialogWidth = dialog.offsetWidth || 600; // fallback width
            const dialogHeight = dialog.offsetHeight || 400; // fallback height

            // Position the upper right corner of the dialog slightly offset from click
            dialog.style.left = `${event.clientX - dialogWidth - 10}px`;

            // Check if cursor is below midpoint of screen height
            const screenMidpoint = window.innerHeight / 2;
            if (event.clientY > screenMidpoint) {
              // Position popup so lower right corner is at click location
              dialog.style.top = `${event.clientY - dialogHeight + 10}px`;
            } else {
              // Position popup so upper right corner is at click location
              dialog.style.top = `${event.clientY - 10}px`;
            }

            dialog.style.zIndex = '1000';
          }
        });
      } catch (error) {
        console.error('Error fetching state history:', error);
        this.updateFooterMessage('Error loading state history');
      }
    },

    closeStateHistoryDialog() {
      this.showStateHistoryDialog = false;
      this.stateHistoryData = {
        lease: [],
        property: [],
      };
      this.stateHistoryType = '';
      this.stateHistoryId = null;
    },

    navigateToLeaseSearch(searchValue) {
      const resolved = this.$router.resolve({
        path: '/lease-search',
        query: { search: searchValue.address.replace('#', ''), hideNav: '1' },
      });
      this.resignIframeTitle = searchValue.address;
      this.resignIframeLoading = true;
      this.resignIframeUrl = resolved.href;
    },

    handleOpenLeaseSearchIframe(address) {
      const resolved = this.$router.resolve({
        path: '/lease-search',
        query: { search: address.replace('#', ''), hideNav: '1' },
      });
      this.resignIframeTitle = address;
      this.resignIframeLoading = true;
      this.resignIframeUrl = resolved.href;
    },

    handleOpenTenantSearchIframe({ search, title }) {
      const resolved = this.$router.resolve({
        path: '/people',
        query: { search, hideNav: '1' },
      });
      this.resignIframeTitle = title || search;
      this.resignIframeLoading = true;
      this.resignIframeUrl = resolved.href;
    },

    handleOpenNewPersonIframe({ email, title }) {
      const resolved = this.$router.resolve({
        path: '/new-person',
        query: {
          search: email,
          email,
          directAdd: '1',
          hideNav: '1',
        },
      });
      this.resignIframeTitle = title || email;
      this.resignIframeLoading = true;
      this.resignIframeUrl = resolved.href;
    },

    async toggleCheckmark(property) {
      const url = '/api/toggle_check/';
      const method = 'PUT';

      const Data = {
        Id: property.propertyId,
        Check: 1 - property.checked,
      };

      axios({
        method: method,
        url: url,
        data: Data,
      })
        .then(() => {
          this.getProperties()
            .then(() => {
              this.sortChecked();
            })
            .catch((error) => {
              console.error('Error fetching properties:', error);
            });
        })
        .catch((error) => {
          console.error('Error toggling checkmark', error);
        });
    },

    selectRow(event, property, index) {
      // Check if the shift key is pressed (shift-click functionality)
      if (event.shiftKey && this.lastSelectedIndex !== null) {
        const start = Math.min(this.lastSelectedIndex, index);
        const end = Math.max(this.lastSelectedIndex, index);

        for (let i = start; i < end; i++) {
          if (!this.selectedProperties.includes(this.filteredProperties[i])) {
            this.selectedProperties.push(this.filteredProperties[i]);
          }
        }
      }
      // Check if the ctrl key (or cmd key on Mac) is pressed (ctrl-click functionality)
      else if (event.ctrlKey || event.metaKey) {
        const propertyIndex = this.selectedProperties.indexOf(property);

        if (propertyIndex === -1) {
          this.selectedProperties.push(property); // Add to selection
        } else {
          this.selectedProperties.splice(propertyIndex, 1); // Remove from selection
        }
      }
      // Regular click, no special keys
      else {
        this.selectedProperties = [property]; // Replace with the clicked row
      }

      // Store the last selected index for shift-click behavior
      this.lastSelectedIndex = index;

      // Update local storage or any other related behavior
      localStorage.setItem(
        'selectedProperties',
        JSON.stringify(this.selectedProperties),
      );
    },

    updateShowNotesPanel(newVal) {
      this.showNotesPanel = newVal; // Update the showNotesPanel state
      if (this.showNotesPanel === false) {
        this.getProperties();
      }
    },

    async fetchLeaseCalendar(year) {
      // Get the selected chart option based on the current index
      const selectedOption = this.chartOptions[this.selectedChart];
      // if we're fetching the lease tab, use the dropdown year

      return axios
        .get(`${selectedOption.url}?year=${year}`)
        .then((response) => {
          this.leaseCalendar = response.data;
        })
        .catch((error) => {
          console.error(`Error fetching ${selectedOption.title}:`, error);
          this.updateFooterMessage(
            `Error fetching ${selectedOption.title}`,
            error,
          );
        });
    },

    clearLeaseSearch() {
      this.leaseSearchQuery = '';
    },

    refreshGrid() {
      this.updateFooterMessage('Getting calendar data...');
      this.fetchLeaseCalendar(this.yearToShow);
    },

    switchGrid() {
      // Increment selectedChart and use modulo to cycle back to 0 when it reaches the end of the chartOptions array
      this.selectedChart = (this.selectedChart + 1) % this.chartOptions.length;

      // Fetch the new lease calendar based on the selected chart
      this.fetchLeaseCalendar(this.year);

      // Store the selected chart index in localStorage
      localStorage.setItem('selectedChart', this.selectedChart);
    },

    async resetAllCheckmarks(state) {
      const promises = [];
      this.showDialog('Are you sure?', ['Confirm', 'Cancel']).then((result) => {
        if (result === 'Confirm') {
          // Create an array to hold all the API call promises
          let checkValue = state === 'set' ? 1 : 0;

          // Loop through all propertyData to set check marks
          this.filteredProperties.forEach((property) => {
            // Create a promise for each PUT request and add it to the promises array
            const promise = axios
              .put('/api/set_check/', {
                Id: property.propertyId,
                Check: checkValue,
              })
              .then(() => {})
              .catch((error) => {
                console.error(
                  `Error setting checkmark for property ID ${property.propertyId}:`,
                  error,
                );
              });

            promises.push(promise);
          });
        }
      });

      // Wait for all API calls to complete
      await Promise.all(promises);

      // Fetch properties after all checkmarks are set
      this.getProperties();
    },

    // Helper function to flatten the lease data into the desired str/ing format
    flattenLeaseCalendar(leaseCalendar) {
      // Ensure leaseCalendar is an array before proceeding
      if (!Array.isArray(leaseCalendar)) {
        console.error('leaseCalendar is not an array', leaseCalendar);
        return leaseCalendar; // Return as is if it's not an array
      }
      // Only flatten if selectedChart is 0
      if (this.selectedChart == 0) {
        return leaseCalendar.map((property) => {
          const months = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec',
          ];
          months.forEach((month) => {
            if (property[month] && typeof property[month] === 'object') {
              // Check if this is a renewal (same tenants)
              const isRenewal = property[month].isRenewal === 1;
              const leaseIdBgColor = isRenewal
                ? ' style="background-color: #a280a6; color: #ffffff; padding-left:9px; padding-right:10px;"'
                : '';

              // Check for rent change and apply color to rent amount
              const rentChange = property[month].rentChange;
              let rentBgColor = '';
              if (rentChange > 0) {
                rentBgColor =
                  ' style=" padding-left:4px; padding-right:4px;background-color: #a6fcbe;"'; // Light green for increase
              } else if (rentChange < 0) {
                rentBgColor =
                  ' style="background-color: #ff050559; padding-left:4px; padding-right:4px;"'; // Light red for decrease
              }

              // Flatten leaseId and monthlyBaseRent into "leaseId\nmonthlyBaseRent"
              property[month] =
                `<small><span${leaseIdBgColor}>${property[month].leaseId}</span></small><br><span${rentBgColor}>$${property[month].monthlyBaseRent}</span>`;
            }
          });
          return property;
        });
      }

      // If selectedChart is not 0, return the leaseCalendar unchanged
      return leaseCalendar;
    },

    async getPaymentStats() {
      try {
        const response = await axios.get('/api/payment-stats', {
          params: {
            daysBefore: this.daysBefore,
            daysAfter: this.daysAfter,
            propertyCount: this.propertyData.length,
          },
        });
        this.paymentCount = response.data.rowCount;
        this.paymentsTotal = response.data.paymentTotal;
        this.paymentPercent = this.paymentCount / this.propertyData.length;

        // Store the chart data from the API response
        this.chartData = response.data.barChartData;
        // Pconsole.log('chartData', this.chartData);
      } catch (error) {
        console.error('Error fetching payment stats:', error);
      }
    },

    toggleDragMode(event) {
      // Set drag mode to true and identify the targeted icon and popup container
      this.isDragMode = !this.isDragMode;

      if (this.isDragMode) {
        // Capture the icon clicked and determine its closest popup container
        const iconElement = event.target;
        const popupContainer = iconElement.closest('.draggable-popup'); // Generalize by class

        if (popupContainer) {
          this.currentPopup = popupContainer;

          // Get bounding rectangles to calculate the offset
          const iconRect = iconElement.getBoundingClientRect();
          const popupRect = popupContainer.getBoundingClientRect();

          // Calculate offset between the icon and the popup's top-left corner
          this.currentIconOffset.x = iconRect.left - popupRect.left;
          this.currentIconOffset.y = iconRect.top - popupRect.top;

          // Add a one-time event listener to capture the next click location
          document.addEventListener('click', this.setPopupPosition);
        }
      } else {
        // If drag mode is off, remove the listener
        document.removeEventListener('click', this.setPopupPosition);
      }
    },

    setPopupPosition(event) {
      // Ensure there is a popup to position
      if (this.currentPopup) {
        // Move the popup to align the icon with the new click location
        this.popupPosition.x = event.clientX - this.currentIconOffset.x;
        this.popupPosition.y = event.clientY - this.currentIconOffset.y;

        // Apply inline styles dynamically to the popup's position
        this.currentPopup.style.left = `${this.popupPosition.x}px`;
        this.currentPopup.style.top = `${this.popupPosition.y}px`;

        // Reset drag mode and remove event listener
        this.isDragMode = false;
        document.removeEventListener('click', this.setPopupPosition);
      }
    },

    bringToFront(event, parentClass = 'draggable-popup') {
      // Find the closest parent with the specified class
      const parentElement = event.target.closest(`.${parentClass}`);
      if (parentElement) {
        // Get the highest z-index currently in use on the page
        const allElements = Array.from(document.querySelectorAll('*'));
        const maxZIndex = allElements.reduce((max, el) => {
          const zIndex = parseFloat(window.getComputedStyle(el).zIndex);
          return isNaN(zIndex) ? max : Math.max(max, zIndex);
        }, 0);

        // Set the parent element's z-index to be one higher than the current highest
        parentElement.style.zIndex = maxZIndex + 1;
      }
    },
    updateFooterMessage(message) {
      // Emit the 'footerMessage' event with the message payload
      eventBus.emit('footerMessage', message);
    },

    openEmailEditor(
      event,
      to = 'dan@brandenburgcapitalmanagement.com',
      subject = 'ledgers attached for Demand Letters',
      body = 'Dan,<br><br> Attached are the ledgers for the current months demand letter recipients, one per email.<br><br>**Pixie Properties Team**<br>**phone:** 616-344-0214<br>',
    ) {
      event.preventDefault();
      eventBus.emit('setEmailDetails', { to, subject, body });
    },

    toggleTooltip(event, property) {
      // If the tooltip is already visible, hide it
      this.selectedProperty = property;
      if (this.activeTooltip) {
        this.activeTooltip = false;
        const tooltip = this.$refs.tooltip;
        if (tooltip) tooltip.style.display = 'none';
        return;
      }
      this.activeTooltip = true;

      this.$nextTick(() => {
        const tooltip = this.$refs.tooltip;

        // Populate the tooltip content
        let content = '';
        if (property.noteTitle) {
          content += marked(`**${property.noteTitle}**<br><hr>`);
        }
        if (property.noteBody) {
          content += `${property.noteBody}`;
          content +=
            '<br><br><small>' +
            this.formatDate(property.noteDate, true) +
            '</small>';
        }
        this.tooltipContent = content || 'No additional notes';

        if (!content) {
          this.handleNotesColumnClick(property);
          this.activeTooltip = false;
          return;
        }

        // Position the tooltip relative to the click event
        const { clientX: mouseX, clientY: mouseY } = event;
        const tooltipWidth = tooltip.offsetWidth;
        const tooltipHeight = tooltip.offsetHeight;
        const viewportHeight = window.innerHeight;

        // Position vertically based on mouse position
        let topPosition = mouseY - tooltipHeight / 2;
        if (topPosition + tooltipHeight > viewportHeight) {
          topPosition = viewportHeight - tooltipHeight - 10; // Prevent overflow at the bottom
        } else if (topPosition < 0) {
          topPosition = 10; // Prevent overflow at the top
        }

        // Position to the left of the click
        let leftPosition = mouseX - tooltipWidth - 10;
        if (leftPosition < 0) {
          leftPosition = 10; // Prevent overflow on the left
        }

        // Apply the calculated positions
        tooltip.style.top = `${topPosition}px`;
        tooltip.style.left = `${leftPosition}px`;
        tooltip.style.display = 'block'; // Show the tooltip
      });
    },

    hideTooltip() {
      this.activeTooltip = false;
    },

    formatDate(dateString, ago = false, short = false) {
      if (short) {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit',
        });
      }

      const date = new Date(dateString);

      // Calculate the difference in milliseconds
      const now = new Date();
      const diffInMs = now - date;

      // Convert the difference to hours
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      const diffInDays = Math.floor(diffInHours / 24);

      // Format the initial date string
      let formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });

      // Append the "ago" text if the parameter is true
      if (ago) {
        if (diffInHours < 24) {
          formattedDate += ` (${diffInHours} hr${
            diffInHours !== 1 ? 's' : ''
          } ago)`;
        } else {
          formattedDate += ` (${diffInDays} day${
            diffInDays !== 1 ? 's' : ''
          } ago)`;
        }
      }

      return formattedDate;
    },

    navigateToPayments(month, property) {
      if (property.address !== 'unmatched to property') {
        return;
      }
      const year = new Date().getFullYear();
      const monthIndex = this.months.indexOf(month);

      // Format the startDate as YYYY-MM-DD
      const startDate = new Date(year, monthIndex, 1)
        .toISOString()
        .split('T')[0];

      /*
      // Use the Vue Router to push the route
      this.$router.push({
        path: '/payments',
        query: { startdate: startDate },
      });
      */

      const url = `${window.location.origin}/properties?tab=payments&startdate=${startDate}`;

      // Open the URL in a new tab
      window.open(url, '_blank');
    },

    showDialog(message, options) {
      this.dialogMessage = message;
      this.dialogOptions = options;
      this.showDialogPopup = true;

      return new Promise((resolve) => {
        this.dialogCallback = resolve;
      });
    },

    onDialogOptionSelected(option) {
      this.showDialogPopup = false;
      if (this.dialogCallback) {
        this.dialogCallback(option);
        this.dialogCallback = null; // Clear the callback
      }
    },

    async recalcRentChg() {
      try {
        this.updateFooterMessage('Recalculating rent changes...');
        await axios.post('/api/recalc-property-rentchg');
        await this.fetchProperties();
        this.updateFooterMessage('Rent changes recalculated.');
      } catch (error) {
        console.error('Error recalculating rent changes:', error);
        this.updateFooterMessage('Error recalculating rent changes.');
      }
    },

    copySelectedAsMarkdown() {
      if (this.selectedProperties.length === 0) {
        alert('No rows selected. Please select rows first.');
        return;
      }

      // Build markdown table header
      const headers = [
        'Address',
        'Specs',
        'Lease Name',
        'Starts',
        'Ends',
        'Tenants',
        'Rent',
        'Bal',
      ];
      let markdown = '| ' + headers.join(' | ') + ' |\n';
      markdown += '| ' + headers.map(() => '---').join(' | ') + ' |\n';

      // Build markdown table rows
      this.selectedProperties.forEach((property) => {
        const row = [
          property.address || '',
          property.specs || '',
          property.name || '',
          property.startsOn || '',
          property.endsOn || '',
          property.tenants || '',
          this.formatNumber(property.monthlyBaseRent) || '',
          this.formatNumber(property.balance) || '',
        ];
        markdown += '| ' + row.join(' | ') + ' |\n';
      });

      const hostname = window.location.hostname;
      const isDev =
        process.env.NODE_ENV === 'development' ||
        hostname === 'localhost' ||
        hostname === 'pixiev2';

      if (isDev) {
        // Show popup with selectable text in DEV
        const popupHtml = `
          <div style="background: white; padding: 20px; border-radius: 8px; max-width: 600px; max-height: 70vh; overflow-y: auto; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
              <h3 style="margin: 0; color: #333;">Markdown Table (${this.selectedProperties.length} rows)</h3>
              <button class="close-popup-btn" style="background: none; border: none; font-size: 24px; cursor: pointer; color: #999;">&times;</button>
            </div>
            <textarea readonly class="markdown-textarea" style="width: 92%; min-height: 200px; padding: 15px; font-family: monospace; font-size: 12px; border: 1px solid #ddd; border-radius: 4px; resize: vertical; cursor: pointer;" title="Click to select all">${markdown}</textarea>
            <button class="copy-markdown-btn" style="margin-top: 10px; padding: 10px 20px; background: #4CAF50; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">Copy to Clipboard</button>
          </div>
        `;

        const overlay = document.createElement('div');
        overlay.style.cssText =
          'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 999999;';
        overlay.innerHTML = `<div>${popupHtml}</div>`;

        document.body.appendChild(overlay);

        const textarea = overlay.querySelector('.markdown-textarea');
        textarea.addEventListener('click', function () {
          this.select();
        });

        const closeBtn = overlay.querySelector('.close-popup-btn');
        closeBtn.addEventListener('click', () => {
          overlay.remove();
        });

        const copyBtn = overlay.querySelector('.copy-markdown-btn');
        copyBtn.addEventListener('click', () => {
          textarea.select();
          textarea.setSelectionRange(0, 99999);
          try {
            const successful = document.execCommand('copy');
            if (successful) {
              overlay.remove();
              this.showMarkdownToast = true;
              setTimeout(() => {
                this.showMarkdownToast = false;
              }, 2000);
            } else {
              alert('Failed to copy to clipboard');
            }
          } catch (err) {
            console.error('Copy failed:', err);
            alert('Failed to copy to clipboard');
          }
        });

        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            overlay.remove();
          }
        });
      } else {
        // Copy directly to clipboard in PRODUCTION
        navigator.clipboard
          .writeText(markdown)
          .then(() => {
            this.showMarkdownToast = true;
            setTimeout(() => {
              this.showMarkdownToast = false;
            }, 2000);
          })
          .catch((err) => {
            console.error('Failed to copy markdown:', err);
            const textArea = document.createElement('textarea');
            textArea.value = markdown;
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
              document.execCommand('copy');
              this.showMarkdownToast = true;
              setTimeout(() => {
                this.showMarkdownToast = false;
              }, 2000);
            } catch (fallbackErr) {
              console.error('Fallback copy failed:', fallbackErr);
              alert('Failed to copy to clipboard');
            }
            document.body.removeChild(textArea);
          });
      }
    },

    exportToExcel() {
      const headers = this.propertyTableHeaders.map((header) => header.label);
      const rows = this.filteredPropertiesTotal.map((property) => {
        return [
          property.expired ? 'Expired' : '-',
          property.address,
          property.name + ' (' + property.leaseId + ')',
          property.startsOn,
          property.endsOn,
          property.tenants,
          this.formatNumber(property.monthlyBaseRent),
          this.formatNumber(property.amount),
          this.formatNumber(property.balance),
          this.displayRatio(property.balanceRatio),
          property.paidOn,
          property.aging,
          property.leaseState,
          property.propertyState,
          property.noteCount > 0 ? 'Notes' : '-',
          property.pixieLastAccess
            ? `${new Date(property.pixieLastAccess).toLocaleString()} (${property.pixieVersion || '?'})`
            : '',
        ];
      });

      // Create a worksheet
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

      // Apply styles
      const wscols = this.propertyTableHeaders.map((header) => ({
        wch: parseInt(header.width),
      }));
      ws['!cols'] = wscols;

      // Styling for headers
      const headerRow = XLSX.utils.encode_row(0);
      for (let i = 0; i < headers.length; i++) {
        const cellAddress = XLSX.utils.encode_col(i) + headerRow;
        if (ws[cellAddress]) {
          ws[cellAddress].s = {
            font: { bold: true },
            alignment: { horizontal: this.getAlignment(headerRow, i) },
          };
        }
      }

      // Styling for data rows
      for (let row = 1; row <= rows.length; row++) {
        for (let col = 0; col < headers.length; col++) {
          const cellAddress =
            XLSX.utils.encode_col(col) + XLSX.utils.encode_row(row);
          if (ws[cellAddress]) {
            ws[cellAddress].s = {
              alignment: { horizontal: this.getAlignment(row, col) },
            };
          }
        }
      }

      // Create workbook and add the worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

      // Generate Excel file and save
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([wbout], { type: 'application/octet-stream' }),
        'table_data.xlsx',
      );
    },

    // Helper method to get alignment from Vue styles
    getAlignment(row, col) {
      if (row === 0) {
        // Header row
        const header = this.propertyTableHeaders[col];
        return header.align || 'center';
      } else {
        const cell = this.$refs.propertyTableBody.querySelector(
          `tr:nth-child(${row}) td:nth-child(${col + 1})`,
        );
        return cell ? window.getComputedStyle(cell).textAlign : 'left';
      }
    },

    inlineScopedCSS(element) {
      // Inject styles specifically for the :id="overviewTab" element
      // Added a footer with "Pixie Properties" and the date and time of printing
      const currentDate = new Date().toLocaleString();

      return (
        `
    <style>
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css">
      /* General body styling */
      body { font-size: 0.75em; padding: 10px; font-family: Arial, sans-serif; line-height: 1.5; color: #333; }

      .search-input-wrapper { display: none;}
      .payment-stats{ display: none;}
      /* OverviewTab and EndingsTab specific styles */
      #overviewTab, #endingsTab { margin: 10px; padding: 15px; border-radius: 5px; width:860px;}
      #overviewTab .tab-title, #endingsTab .tab-title { font-size: 1.5em; font-weight: bold; margin-bottom: 10px; }
      #overviewTab .tab-section, #endingsTab .tab-section { margin-top: 15px; }
      #overviewTab .tab-section-title, #endingsTab .tab-section-title { font-size: 1.2em; font-weight: bold; margin-bottom: 5px; }
      #overviewTab .tab-section-content, #endingsTab .tab-section-content { font-size: 1em; color: #555; }
      #overviewTab .tab-highlight, #endingsTab .tab-highlight { background-color: #eef; padding: 10px; border-left: 3px solid #007bff; margin: 10px 0; }
      #overviewTab tbody, #endingsTab tbody {font-size:0.75em !important}

      /* Table styles */
      #overviewTab .transaction-table, #endingsTab .transaction-table { width: 100%; margin-top: 10px; font-size: 0.75em }
      #overviewTab .transaction-table th, #overviewTab .transaction-table td, #endingsTab .transaction-table th, #endingsTab .transaction-table td { border: 1px solid #ccc; padding: 8px; text-align: left; }
      #overviewTab .transaction-table th, #endingsTab .transaction-table th { background-color: #f1f1f1; font-weight: bold; }
      .even-row { background-color: #e0e0e0 !important;  }
      .odd-row { background-color: #ffffff !important; }

      /* Footer styling */
      #overviewTab .footer, #endingsTab .footer { text-align: center; font-size: 0.9em; color: #888; margin-top: 30px; width: 860px; }

      #leasesByMonth thead { font-size: 9pt }
      #leasesByMonth tr td:first-child { font-size: 9pt; padding-left:5px }
    </style>
    ` +
        element +
        `
    <div style="text-align: center; font-size: 0.9em; color: #888; margin-top: 30px; width: 860px;" >
      <p>Pixie Properties</p>
      <p>${currentDate}</p>
    </div>
    `
      );
    },

    async printElement(elementName, mode = 'print') {
      try {
        const element = document.getElementById(elementName);
        if (!element) {
          console.error('Element not found:', elementName);
          return null; // Return null if element is not found
        }

        // Clone the element to avoid modifying the original HTML
        const clonedElement = element.cloneNode(true);

        // Select all rows in the table and hide the last two columns
        clonedElement.querySelectorAll('tr').forEach((row) => {
          const cells = row.querySelectorAll('td, th');
          if (cells.length >= 2) {
            cells[1].style.width = '130px';
            cells[4].style.width = '110px';
            cells[5].style.width = '70px';
          }
          if (
            cells.length >= 6 &&
            ['overviewTab', 'endingsTab'].includes(elementName)
          ) {
            // cells[cells.length - 1].style.display = 'none';
            // cells[cells.length - 2].style.display = 'none';
            cells[cells.length - 4].style.display = 'none';
            cells[cells.length - 5].style.display = 'none';
          }
        });

        const htmlContentWithCSS = this.inlineScopedCSS(
          clonedElement.outerHTML,
        );
        // Replace <span data-v-... class="toggle-check"><i data-v-... class="toggle-check fas fa-check"></i></span> with ✓
        const updatedHtmlContent = htmlContentWithCSS.replace(
          /<span\s+data-v-[^=]*?=["'].*?["']\s+class=["']toggle-check["']>\s*<i\s+data-v-[^=]*?=["'].*?["']\s+class=["']toggle-check fas fa-check["']><\/i>\s*<\/span>/g,
          '<td style="text-align: center">✓</td>',
        );

        if (mode === 'preview') {
          // Create a container for the iframe and title bar
          const container = document.createElement('div');
          container.style.position = 'fixed';
          container.style.top = '50%';
          container.style.left = '50%';
          container.style.transform = 'translate(-50%, -50%)';
          container.style.width = '1200px';
          container.style.height = '80%';
          container.style.border = '1px solid #ccc';
          container.style.background = '#ddd';
          container.style.zIndex = '1000'; // Ensure it's on top of other elements
          container.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.5)';

          // Create the title bar
          const titleBar = document.createElement('div');
          titleBar.style.height = '40px';
          titleBar.style.background = '#333';
          titleBar.style.color = '#fff';
          titleBar.style.display = 'flex';
          titleBar.style.alignItems = 'center';
          titleBar.style.justifyContent = 'space-between';
          titleBar.style.padding = '0 10px';
          titleBar.style.boxSizing = 'border-box';

          // Title text
          const titleText = document.createElement('span');
          titleText.innerText = 'Preview';
          titleBar.appendChild(titleText);

          // Close button
          const closeButton = document.createElement('button');
          closeButton.innerText = 'Close';
          closeButton.style.marginLeft = 'auto';
          closeButton.style.backgroundColor = '#ff5c5c';
          closeButton.style.border = 'none';
          closeButton.style.color = 'white';
          closeButton.style.padding = '5px 10px';
          closeButton.style.cursor = 'pointer';

          closeButton.addEventListener('click', () => {
            if (document.body.contains(container)) {
              document.body.removeChild(container);
            }
          });

          titleBar.appendChild(closeButton);

          // Create an iframe to display the HTML content for preview
          const iframe = document.createElement('iframe');
          iframe.style.width = '100%';
          iframe.style.height = 'calc(100% - 40px)';
          iframe.style.border = 'none';

          // Append title bar and iframe to container
          container.appendChild(titleBar);
          container.appendChild(iframe);

          document.body.appendChild(container);

          // Write the HTML content with inline styles to the iframe
          iframe.contentDocument.open();
          iframe.contentDocument.write(updatedHtmlContent);
          iframe.contentDocument.close();
        } else {
          const response = await axios.post(
            '/api/convert-to-pdf',
            { html: updatedHtmlContent },
            { responseType: 'blob' },
          );

          const pdfBlob = new Blob([response.data], {
            type: 'application/pdf',
          });
          const url = URL.createObjectURL(pdfBlob);

          if (mode === 'print') {
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);

            iframe.onload = () => {
              if (iframe.contentWindow) {
                iframe.contentWindow.print();
                setTimeout(() => {
                  if (document.body.contains(iframe)) {
                    document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                    this.showFooterMessage(
                      'Removed print preview after 10 seconds.',
                    );
                  }
                }, 2000000); // Adjust delay as needed
              }
            };
          } else if (mode === 'email') {
            return pdfBlob; // Return the Blob for email attachment
          }
        }
      } catch (error) {
        console.error('Error generating output:', error);
        return null; // Return null if there was an error
      }
    },

    printTable() {
      const elementId =
        this.activeTab === 'endings' ? 'endingsTab' : 'overviewTab';
      this.printElement(elementId);
    },

    showFooterMessage(message) {
      // Set the footer message
      eventBus.emit('footerMessage', message);
    },
  },

  watch: {
    selectedResignId(newVal) {
      // Clear any existing timeout
      if (this.resignEditHintTimeout) {
        clearTimeout(this.resignEditHintTimeout);
      }
      if (newVal) {
        // Show hint when a row is selected
        this.showResignEditHint = true;
        // Hide after 8 seconds
        this.resignEditHintTimeout = setTimeout(() => {
          this.showResignEditHint = false;
        }, 8000);
      } else {
        this.showResignEditHint = false;
      }
    },

    showChart(newVal) {
      if (newVal) {
        this.getPaymentStats();
      }
    },

    yearToShow(newVal) {
      this.fetchLeaseCalendar(newVal);
    },

    searchQuery(newVal) {
      if (newVal === ':!b') {
        this.sortField = 'balance';
        this.sortOrder = 'asc';
        this.sortProperties();
      }

      if (newVal === ':s') {
        this.sortField = 'leaseState';
        this.sortOrder = 'asc';
        this.sortProperties();
      }

      if (newVal === ':p') {
        this.sortField = 'aging';
        this.sortOrder = 'asc';
        this.sortProperties();
      }

      if (newVal === ':b') {
        this.sortField = 'balance';
        this.sortOrder = 'desc';
        this.sortProperties();
      }

      if (newVal === ':z') {
        this.sortField = 'monthlyBaseRent';
        this.sortOrder = 'desc';
        this.sortProperties();
      }
    },

    '$route.query.tab'(newTab) {
      if (newTab) {
        // Map URL aliases to internal tab names
        let tabName = newTab;
        if (newTab === 'early') {
          tabName = 'resigns';
        }
        this.switchTab(tabName);
      }
    },

    showStatesDialog(newVal) {
      if (newVal === false) {
        this.getProperties();
      }
    },

    daysBefore() {
      this.getPaymentStats();
    },

    daysAfter() {
      this.getPaymentStats();
    },
  },
};
</script>

<style scoped>
/* Lease Endings Year Buttons */
.le-year-buttons {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  gap: 2px;
}

.le-year-button {
  background-color: #e8e8e8;
  border: 1px solid #ccc;
  border-radius: 2px;
  padding: 1px 4px;
  font-size: 8px;
  color: #343232;
  cursor: pointer;
  width: 16px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.le-year-button:hover {
  background-color: #d0d0d0;
  color: #333;
}

.le-year-selected {
  background-color: #888;
  color: #fff;
}

.le-year-selected:hover {
  background-color: #666;
  color: #fff;
}

.le-year-hidden {
  visibility: hidden;
}

/* Main Page Layout */
.properties-page {
  padding: 0;
  width: 1200px;
  height: 95%;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Page Title Styling */
.page-title {
  font-size: 14pt;
  font-weight: bold;
  margin-bottom: 10px;
}
.search-input-wrapper {
  background: #dddddd;
  margin-top: 5px;
  padding-left: 4px;
  padding-bottom: 5px;
  padding-top: 5px;
  font-size: 0.9em;
  font-weight: normal;
  margin-right: -13px;
}

.search-input-wrapper label,
.search-input-wrapper span {
  font-weight: normal;
}

/* Search Bar Styling */
.search-bar {
  display: flex; /* Use flexbox for alignment */
  align-items: center;
  gap: 10px; /* Space between elements */
  margin-top: 15px;
  font-weight: bold;
  font-size: 0.9em;
  margin-bottom: 5px;
  margin-right: 15px;
}

/* Search Input Field */
.search-bar input {
  height: 20px;
  width: 350px;
  border-radius: 4px;
  border: 1px solid #ccc;

  padding-left: 5px;
}

/* Clear Button */
.search-bar button {
  height: 23px;
  border-radius: 4px;
  padding: 1px;
  border: 1px solid #878787;
  margin-left: -11px;
  background: #bbb;
  font-size: 0.8rem;
}

/* Label Inside Search Bar */
.search-bar .label {
  margin-bottom: 0;
}

/* Expiring Input Container */
.expiring-input {
  display: flex;
  align-items: center;
  gap: 5px; /* Space between label, input, and text */
  padding-right: 10px;
}

/* Expiring Days Input */
.expiring-days-input {
  width: 31px !important; /* Width of the number input */
}

/* Table Container */
.table-container {
  position: relative;
  width: 1163px;
  overflow: hidden;
  padding-bottom: 5px;
  height: 615px;
}

/* Table Header Styling */
.table-header {
  overflow: hidden; /* Prevents scrollbar on the header */
  margin-bottom: -1px;
  width: 1168px;
}

.table-body-wrapper {
  max-width: 1175px;
  overflow: hidden;
}

/* Icon Styling in Table Header */
.table-header i {
  padding-left: 4px;
}

/* Scrollable Table Body */
.table-body {
  max-height: 570px; /* Set a height for the scrollable area */
  overflow-y: scroll; /* Enables vertical scrolling */
  display: block; /* Makes the body a block element for separate scrolling */
  overflow-x: hidden;
  width: 1183px;
}

/* Table Styling for Both Header and Body */
.table-body table,
.table-header table {
  width: 1165px; /* Ensures the table takes the full width of its container */
  border-collapse: collapse;
  font-size: 9pt;
  border: 0;
}

/* Table Header and Data Cell Styling */
th,
td {
  text-align: left;
  padding: 3px; /* Add padding for better spacing */
  border-bottom: 0.1em solid #e4e4e4;
  border-left: 1pt solid #ccc;
  box-sizing: border-box;
}

/* Table Header Specific Styling */
th {
  min-width: 50px; /* Sets a minimum width for headers */
}

/* Sticky Table Header */
thead {
  background-color: #888;
  font-size: 1em;
  position: sticky; /* Keeps the header sticky */
  top: 0; /* Sticks the header to the top of the container */
}

/* Table Row Styling */
tbody tr {
  table-layout: auto; /* Allows columns to adjust based on content and width settings */
  cursor: pointer;
}

.table-body tr td:first-child {
  padding-left: 5px;
}

/* Selected Row Highlight */
.selected-row {
  background-color: #baa1bd !important; /* Highlight selected row */
}

/* Row Colors for Alternating Effect */
.odd-row {
  background-color: #ececec; /* Example alternating row color */
}

.even-row {
  background-color: #ffffff;
}

.last-row {
  background: #666;
  color: #eee;
}

/* Expired Icon Styling */
.is-expired {
  color: #5f53c1a1;
  /* font-size: 1.1em; */
}

/* Highlight Rows That Are Expiring Soon */
.expiring-soon {
  background-color: #ffebcc; /* Light yellow background for expiring soon rows */
}

.lease-is-expired {
  background-color: #ffcccc;
  color: #cc0000;
  font-weight: bold;
}

.payment-warning {
  background-color: #ffebcc; /* Light yellow background for expiring soon rows */
}

.payment-recent {
  background-color: #9adba6; /* Light yellow background for expiring soon rows */
}
.toggle-check {
  color: green;
}

.pixie-access-icon {
  color: #888;
}

.pixie-access-today {
  color: #4caf50;
}

.tab-header {
  display: flex;
  margin-bottom: 0;
  user-select: none;
}

.tab-header button {
  flex: 1;
  padding: 10px;
  cursor: pointer;
  background-color: #a7a7a7;
  border: 1px solid #939393;
  outline: none;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

.tab-header button.active {
  background-color: #a898b7;
  font-weight: bold;
  border-bottom: 3px solid #7e6a81;
}

.tab-content {
  border: 1px solid #ccc;
  padding: 15px;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.1);
  background: #ddd;
  user-select: none;
  min-height: 732px;
}

/* Search Bar Styling */
.search-bar {
  display: flex; /* Use flexbox for alignment */
  justify-content: space-between; /* Space between elements */
  align-items: center;
  margin-top: 15px;
  font-weight: bold;
  font-size: 0.9em;
  margin-bottom: 5px;
}

/* Container for search input and clear button */
.search-left {
  display: flex; /* Use flexbox for alignment */
  align-items: center;
  gap: 10px; /* Space between elements */
}

.search-right {
  display: flex; /* Use flexbox for layout */
  justify-content: flex-end; /* Align items to the right */
}

.input-group {
  display: flex; /* Use flexbox for horizontal layout */
  gap: 20px; /* Add some space between the two input groups */
  align-items: center; /* Align items vertically */
}

.recent-start,
.expiring-input {
  display: flex; /* Flexbox to align label and input horizontally */
  align-items: center; /* Center-align vertically */
  gap: 5px; /* Add some space between the label and input */
}

/* Toggle Button */
.toggle-button {
  height: 23px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  background: #bbb;
  cursor: pointer;
  margin-right: 5px;
}

.toggle-mode {
  padding-right: 10px;
}

.monthly-summary {
  padding-top: 3px;
  font-size: 0.9em;
  font-family: monospace;
  color: #efefef;
  font-weight: normal;
}
/* new CSS for editing ledger entries */
.ledger-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 400px;
  background-color: white;
  border: 1px solid #ddd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
  z-index: 999;
}

.ledger-popup-content {
  display: flex;
  flex-direction: column;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.button-row {
  display: flex;
  justify-content: space-around;
  margin: 10px 0;
}

.popup-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
}

.popup-button i {
  font-size: 24px;
}

.popup-button.active {
  background-color: #eee;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
}
.report-title {
  font-weight: bold;
  font-size: 1.3em;
}

.footer-message {
  transition: background-color 0.5s ease; /* Smooth transition for the background fade */
  position: fixed; /* Position it absolutely relative to the parent container */
  border-radius: 4px;
  bottom: 0; /* Align it to the bottom of the parent container */
  left: 0;
  width: 100vw; /* Make it span the full width of the parent container */
  height: 20px; /* Set the desired height */
  text-align: left; /* Optional: Center text horizontally */
  line-height: 20px; /* Optional: Center text vertically within the 20px height */
  font-size: 0.8em;
  padding: 3px;
  padding-left: 6px;
  z-index: 100000; /* Ensure the footer stays on top of other elements */
  width: 1220px;
}

.footer-message.highlighted {
  border: 1pt solid #ddd;
  background-color: #9b7ba8; /* Your desired background color */
  color: white;
}

.protected-page {
  margin-left: 25px;
}

.payment-stats {
  position: relative;
  text-align: right;
  font-size: 0.8em;
  margin-top: 12px;
  margin-bottom: 5px;
  padding-right: 5px;
  left: 0;
  bottom: 0;
}

.payment-stats input {
  margin-left: 3px;
  width: 32px;
  text-align: right;
  border-radius: 4px;
  border: 1pt solid #aaa;
}

.chart-container {
  position: fixed;
  left: 100px;
  top: 150px;
  box-shadow: 11px 9px 7px 5px rgba(0, 0, 0, 0.3);
  border: 1pt solid #898484;
  width: 955px;
  background: white;
  padding: 10px;
  border-radius: 8px;
}

.notes-column .fa-sticky-note {
  color: #b2afaf;
}

.email-multiple {
  position: absolute;
  left: 0px;
  bottom: -5px;
  cursor: pointer;
}

/* Modal background */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 600px;
  box-shadow: 7px 7px 8px 4px rgba(0, 0, 0, 0.2);
  position: fixed;
  top: 100px;
  left: 150px;
}

.modal-content h3 {
  margin-top: 0;
  font-size: 1.5em;
  text-align: center;
}

/* Form group container for label and input */
.form-group {
  display: flex;
  align-items: center;
  margin-top: 15px;
}

/* Consistent label width */
.form-group label {
  width: 80px;
  /* font-weight: bold; */
  margin-right: 10px;
  text-align: left;
  font-size: 0.85em;
}

/* Style for inputs and textarea */
.form-group input[type='email'],
.form-group input[type='text'],
.form-group textarea {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.form-group textarea {
  margin-right: 0;
  min-height: 100px;
}

/* File input */
.form-group input[type='file'] {
  padding: 5px;
  flex: 1;
}

/* Buttons */
.modal-content .button-wrapper {
  text-align: right;
}

.modal-content .button-wrapper button {
  width: 115px;
}

.modal-content button {
  margin-top: 15px;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85em;
}

.modal-content button:first-of-type {
  border: 1pt solid #888;
  background-color: #ccc;
  color: #333;
  margin-right: 10px;
}

.modal-content button:last-of-type {
  border: 1pt solid #888;
  background-color: #ccc;
  color: #333;
}

/* File input */
.modal-content input[type='file'] {
  padding: 5px;
}

.quick-email-title {
  font-size: 1.4em;
  margin-top: -20px;
  margin-left: -20px;
  margin-right: -20px;
  background: #333;
  padding: 10px;
  color: #ddd;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.preview {
  border-radius: 5px;
  /* margin-top: 10px; */
  margin-left: 11px;
  border: 1px solid #ddd;
  padding: 5px;
  min-height: 250px;
  max-height: 250px;
  overflow-y: auto;
  font-size: 0.8em;
  width: 378px;
}

.modal-content .list-button {
  padding: 2px;
  height: 21px;
  width: 96px;
  margin-top: -5px;
  margin-left: 4px;
  background-color: #eee !important;
  font-family: arial;
  font-size: 0.8em;
  font-weight: 500;
  border-radius: 2px;
}

.markdown-help {
  font-size: 0.8em;
  margin-left: 90px;
  margin-top: -13px;
}
/* ledger list styles */
.ledger-list-popup {
  position: absolute;
  top: 50%;
  left: 50%;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  width: 650px;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.ledger-list-title {
  margin-top: -21px;
  margin-left: -21px;
  margin-right: -21px;
  margin-bottom: 2px;
  color: #eee;
  background-color: #333;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  padding: 8px;
}

.ledger-list-popup .ll-button-row {
  text-align: right;
}

.ledger-list-popup button {
  margin-left: 4px;
}

.ledger-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
  font-size: 0.85em;
}

.ll-list-wrapper {
  height: 350px;
  overflow-y: auto;
  font-size: 0.9em;
}

.ledger-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.ledger-list li:nth-child(even) {
  background: #ddd;
}

.ledger-list li span {
  flex: 1;
  text-align: left;
}

.ledger-list button {
  background: none;
  border: none;
  cursor: pointer;
  color: #555;
}

.ledger-list button i {
  font-size: 1.2em;
}

.ledger-list button {
  margin-top: 10px;
  padding: 5px 10px;
  cursor: pointer;
}

/* attachment viewer */
.attachment-viewer {
  position: absolute;
  top: 20%;
  left: 5%;
  width: 790px;
  height: 460px;
  border: 1px solid rgb(145, 146, 146);
  overflow: hidden;
  display: flex;
  flex-direction: column; /* Ensures the header is above the iframe */
  background-color: #fff;
  border-radius: 5px;
  z-index: 9999;
}

/* Title bar styles */
.attachment-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #333;
  color: #eee;
  padding: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
}

.attachment-viewer-title {
  font-weight: bold;
  text-align: center;
  flex-grow: 1;
}

.attachment-viewer-close-button {
  background: transparent;
  color: #eee;
  border: none;
  font-size: 16px;
  cursor: pointer;
  position: relative;
  right: 20px;
}

.attachment-viewer-close-button:hover {
  color: #ff6666; /* Optional: change color on hover */
}

/* iframe styles */
.attachment-viewer iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.ledger-list-popup .button-wrapper {
  margin-top: 6px;
  margin-left: -4px;
}

/* for draggable */

.draggable-icon {
  cursor: pointer;
  color: #888;
  transition: color 0.3s;
  position: absolute;
  right: 10px;
}

.draggable-icon.active {
  color: #4a90e2;
}

/* for tooltip */
.custom-tooltip {
  max-width: 200px;
  background-color: #e9ec99;
  color: #000;
  padding: 8px;
  border-radius: 3px;
  box-shadow: 6px 6px 10px 4px rgba(0, 0, 0, 0.3);
  position: absolute;
  display: none;
  z-index: 30000;
  font-size: 12px;
  cursor: pointer;
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}

.custom-tooltip :deep(p) {
  margin: 0;
}

.clear-button {
  margin-left: 3px;
  margin-right: 3px;
  width: 26px;
}

.confirmation-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.confirmation-popup-title {
  margin: -20px;
  margin-bottom: 10px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  background: #333;
  color: #eee;
  padding: 8px;
}

.confirmation-popup-content {
  position: absolute;
  top: 300px;
  left: 150px;
  width: 300px;
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 9px 13px 7px 4px rgb(0 0 0 / 22%);
  z-index: 2000;
  user-select: none;
}

.confirmation-popup-buttons button {
  margin: 10px;
  padding: 10px 20px;
  background-color: #a080a8;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.confirmation-popup-buttons button:hover {
  background-color: #776279;
}

.markdown-toast {
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #4caf50;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 999999;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: fadeInOut 2s ease-in-out;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
  15% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  85% {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateX(-50%) translateY(-10px);
  }
}

.strip-chart-wrapper {
  position: absolute;
  left: 46px;
  top: 140px;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: start;
  align-items: center;
}

.property-square {
  width: 7px;
  height: 8px;
  display: block;
  border-radius: 3px;
}

.copy-panel {
  width: 284px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  top: 50%;
  left: 50%;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.8em;
  border: 1px solid #888;
  padding: 6px;
  border-radius: 5px;
  cursor: pointer;
}

.copy-panel label {
  margin-top: 5px;
  font-weight: bold;
}

.copy-panel-header {
  margin: -6px;
  height: 18px;
  background: #333;
  color: #fff;
  padding: 5px;
  text-align: center;
  margin-bottom: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-size: 1.1em;
}
.copy-message {
  background: green;
  color: #fff;
  font-size: 9px;
  margin-top: 5px;
  margin-bottom: 5px;
  transition: opacity 0.5s ease-out;
  padding: 4px;
  min-height: 10px;
  height: 10px;
}

.copy-panel {
  width: 284px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  top: 50%;
  left: 50%;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.8em;
  border: 1px solid #888;
  padding: 6px;
  border-radius: 5px;
  cursor: pointer;
}

.copy-panel label {
  margin-top: 5px;
  font-weight: bold;
}

.copy-panel-header {
  margin: -6px;
  height: 18px;
  background: #333;
  color: #fff;
  padding: 5px;
  text-align: center;
  margin-bottom: 10px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-size: 1.1em;
}
.copy-message {
  background: green;
  color: #fff;
  font-size: 9px;
  margin-top: 5px;
  margin-bottom: 5px;
  transition: opacity 0.5s ease-out;
  padding: 4px;
  min-height: 10px;
  height: 10px;
}
.copy-panel-item:hover {
  background: #dfbee4;
}

.mini-calc {
  margin-top: 10px;
}

.mini-calc input {
  border-radius: 5px;
  border: 1pt solid #ddd;
  outline: none;
  font-size: 0.9em;
}

.has-renewal {
  border: 2pt solid lightgreen;
}

.specs-dialog {
  width: 300px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.9em;
  border: 1px solid #888;
  border-radius: 5px;
}

.specs-dialog-header {
  background: #333;
  color: #fff;
  padding: 8px;
  text-align: center;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-weight: bold;
}

.specs-dialog-content {
  padding: 15px;
}

.specs-dialog-content label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.specs-select,
.specs-input {
  width: 100%;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 3px;
  margin-bottom: 10px;
  font-size: 0.9em;
}

.new-specs-input {
  margin-top: 10px;
}

.specs-dialog-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

.save-button,
.close-button {
  flex: 1;
  padding: 8px;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-size: 0.9em;
}

.save-button {
  background: #007bff;
  color: white;
}

.save-button:hover {
  background: #0056b3;
}

.close-button {
  background: #6c757d;
  color: white;
}

.close-button:hover {
  background: #545b62;
}

.rentchg-cell-stack {
  display: flex;
  flex-direction: column;
}

.rentchg-cell-line {
  padding: 1px 4px;
  line-height: 1.15;
}

.rentchg-dialog {
  width: 300px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.9em;
  border: 1px solid #888;
  border-radius: 5px;
}

.rentchg-dialog-header {
  background: #333;
  color: #fff;
  padding: 8px;
  text-align: center;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-weight: bold;
}

.rentchg-dialog-content {
  padding: 15px;
}

.rentchg-dialog-content label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.rentchg-select,
.rentchg-input {
  width: 100%;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 3px;
  margin-bottom: 10px;
  font-size: 0.9em;
}

.new-rentchg-input {
  margin-top: 10px;
}

.rentchg-dialog-buttons {
  display: flex;
  gap: 10px;
  margin-top: 15px;
}

/* State History Dialog Styles */
.state-history-dialog {
  width: 600px;
  z-index: 9999;
  background: #fff;
  position: absolute;
  box-shadow: 8px 7px 10px 6px rgba(0, 0, 0, 0.25);
  font-size: 0.9em;
  border: 1px solid #888;
  border-radius: 5px;
}

.state-history-dialog-header {
  background: #333;
  color: #fff;
  padding: 8px;
  text-align: center;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  font-weight: bold;
}

.state-history-dialog-content {
  padding: 15px;
}

.state-history-columns {
  display: flex;
  gap: 20px;
}

.state-column {
  flex: 1;
}

.state-column h4 {
  margin: 0 0 10px 0;
  padding: 5px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  text-align: center;
  border-radius: 3px;
}

.state-table {
  border: 1px solid #dee2e6;
  border-radius: 3px;
  font-size: 0.85em;
}

.state-table-header {
  display: grid;
  grid-template-columns: 80px 50px 1fr;
  gap: 5px;
  padding: 8px;
  background: #e9ecef;
  font-weight: bold;
  border-bottom: 1px solid #dee2e6;
}

.state-table-row {
  display: grid;
  grid-template-columns: 80px 50px 1fr;
  gap: 5px;
  padding: 6px 8px;
  border-bottom: 1px solid #f8f9fa;
}

.state-table-row:nth-child(even) {
  background: #f8f9fa;
}

.state-table-row:hover {
  background: #e3f2fd;
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #6c757d;
  font-style: italic;
}

.state-history-dialog-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}

/* ============================================
   RESIGN TAB STYLES
   ============================================ */
.resigns-tab {
  padding: 10px;
}

.resigns-toolbar {
  display: flex;
  gap: 3px;
  margin-bottom: 10px;
  align-items: center;
}

.resign-filter-input {
  flex: 1;
  max-width: 300px;
  padding: 6px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
}

.resigns-toolbar button {
  height: 23px;
  border-radius: 4px;
  padding: 1px;
  border: 1px solid #878787;
  margin-left: 3px;
  margin-right: 3px;
  background: #bbb;
  font-size: 0.8rem;
  width: 26px;
}

.resigns-toolbar button:hover {
  background: #999;
}

.resign-edit-hint {
  font-size: 12px;
  color: #666;
  margin-left: 10px;
  animation: fadeOut 8s ease-in-out forwards;
}

@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  75% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.edit-btn:disabled:hover {
  background: #f8f8f8;
}

.resigns-table-wrapper {
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.resigns-table-wrapper::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.resigns-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 9pt;
  line-height: 1.2;
}

.resigns-table th {
  background: #888;
  color: #fff;
  padding: 2px 3px;
  text-align: left;
  border-bottom: 1px solid #ccc;
  border-left: 1px solid #ccc;
  position: sticky;
  top: 0;
  z-index: 1;
}

.resigns-table th.sortable-th {
  cursor: pointer;
  user-select: none;
}

.resigns-table th.sortable-th:hover {
  background: #666;
}

.resigns-table th i.fas {
  margin-left: 3px;
  font-size: 0.8em;
}

.resigns-table td {
  padding: 1px 3px;
  border-bottom: 0.1em solid #e4e4e4;
  border-left: 1pt solid #ccc;
}

.resigns-table input[type='checkbox'] {
  margin: 0;
  padding: 0;
  vertical-align: middle;
}

.resigns-table tbody tr:nth-child(odd) {
  background-color: #ddd;
}

.resigns-table tbody tr:nth-child(even) {
  background-color: #fff;
}

.resigns-table tbody tr.selected-row {
  background-color: #baa1bd !important;
}

.resigns-table tr:hover {
  background: #f5f5f5;
  cursor: pointer;
}

.resign-completed {
  background: #e8f5e9 !important;
}

.resign-completed:hover {
  background: #c8e6c9 !important;
}

.tenants-cell {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resign-tenants-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resign-address-cell {
  max-width: 130px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.note-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resign-status-cell {
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  max-width: 110px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.status-docusign-needed {
  background: #fff3cd;
  color: #856404;
}

.status-resigning-in-progress {
  background: #cce5ff;
  color: #004085;
}

.status-completed {
  background: #d4edda;
  color: #155724;
}

.status-lease-created {
  background: #d1e7dd;
  color: #0f5132;
}

.status-lease-downloaded {
  background: #e2e3e5;
  color: #41464b;
}

.status-document-uploaded {
  background: #d1ecf1;
  color: #055160;
}

.resigns-summary {
  margin-top: 10px;
  padding: 8px;
  background: #f8f8f8;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
}

/* Resign Edit Dialog */
.resign-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.resign-dialog {
  position: fixed;
  top: 275px;
  left: 252px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  width: 450px;
}

.resign-dialog-header {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ddd;
}

.resign-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.resign-dialog-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.resign-dialog-row label {
  width: 120px;
  font-weight: 500;
  font-size: 13px;
}

.resign-input,
.resign-select {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
}

.resign-textarea {
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  resize: vertical;
}

.resign-dialog-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #ddd;
}

.resign-dialog-buttons .save-button {
  background: #28a745;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.resign-dialog-buttons .save-button:hover {
  background: #218838;
}

.resign-dialog-buttons .close-button {
  background: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
}

.resign-dialog-buttons .close-button:hover {
  background: #5a6268;
}

:deep(.resign-search-highlight) {
  background: #ecdcf2;
  color: #555;
}

.resign-iframe-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  z-index: 30000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.resign-iframe-container {
  width: 90vw;
  max-width: 1400px;
  height: 100vh;
  background: #fff;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
}

.resign-iframe-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 14px;
  background: #333;
  color: #eee;
  font-size: 0.95em;
  flex-shrink: 0;
}

.resign-iframe-close-btn {
  background: #555;
  border: 1px solid #777;
  color: #eee;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7em;
}

.resign-iframe-close-btn:hover {
  background: #d32f2f;
}

.resign-iframe-scroll-wrapper {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.resign-iframe-spinner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #1e1e2e;
  z-index: 1;
  gap: 12px;
  color: #ccc;
  font-size: 1.1em;
}

.resign-iframe-spinner-overlay .fa-spinner {
  font-size: 2em;
  color: #4fc3f7;
}

.resign-lease-iframe {
  border: none;
  width: 100%;
  height: 100%;
}
</style>
